"use client";

import { useState, useEffect, useCallback, useRef, ChangeEvent, useMemo } from "react";
import {
  supportsSpeakerphone,
  setSpeakerphone as setSpeakerphoneNative,
  configureAudioForCall,
  releaseAudioSession,
} from "@/lib/capacitor/audio-session";
import { useRouter, usePathname } from "next/navigation";
import CoachWaveform from "@/app/(authenticated)/agency-dashboard/_module/components/agency/CoachWaveform";
import CoachAudioControls from "@/app/(authenticated)/agency-dashboard/_module/components/agency/CoachAudioControls";
import { useCoachSession } from "@/app/_module/contexts/CoachSessionContext";
import {
  Phone,
  PhoneOff,
  Mic,
  MicOff,
  Pause,
  PauseCircle,
  Play,
  Zap,
  Grid3X3,
  Hash,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  Delete,
  User,
  ExternalLink,
  Users,
  UserPlus,
  Loader2,
  X,
  Sparkles,
  MapPin,
  Calendar,
  Clock,
  Mail,
  UserCheck,
  Trash2,
  Info,
  CheckCircle,
  StickyNote,
  ArrowRightLeft,
  PhoneForwarded,
  Undo2,
  PhoneOutgoing,
  FileText,
  Crosshair,
  Settings,
  ShoppingCart,
  Target,
  RotateCcw,
  Headphones,
  Volume2,
  VolumeX,
  Radio,
  Eye,
  EyeOff,
  Megaphone,
} from "lucide-react";

import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import { ScrollArea } from "@/app/_module/components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/app/_module/components/ui/dropdown-menu";
import { Input } from "@/app/_module/components/ui/input";
import { useToast } from "@/app/_module/hooks/use-toast";
import { useVoiceDeviceContext } from "@/app/_module/contexts/VoiceDeviceProvider";
import { wasManualCallAutoStopped, clearManualCallAutoStopped } from "@/app/_module/contexts/TwilioDeviceContext";
import { useUnifiedDialer } from "@/app/_module/contexts/UnifiedDialerContext";
import { BulkSourceOptions, BulkTagOptions } from "@/app/_module/components/BulkLeadOptions";
import { useIncomingCall } from "@/app/_module/contexts/IncomingCallContext";
import { useSmsConversation } from "@/app/_module/contexts/SmsConversationContext";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { formatPhoneNumber } from "@/app/_module/utils/formatters";
import { InsuraABotKeyGrabber } from "@/app/(authenticated)/communication/_modules/components/InsuraABotKeyGrabber";
import { DTMFPollingWrapper, type DTMFPollingWrapperRef } from "@/app/(authenticated)/communication/_modules/components/DTMFPollingWrapper";
import type { DTMFDigit } from "@/app/_module/services/dtmfService";
import { useQueryClient } from "@tanstack/react-query";
import { useLeadByPhone, useLead, useCreateLead, usePipelineStages, useBulkUpdateLeadStatus, useUpdateLeadStatus } from "@/app/(authenticated)/leads/_modules/hooks/queries";
import { leadsQueryKeys } from "@/app/(authenticated)/leads/_modules/services/keys";
import { getStatusDisplayName, LeadStatus } from "@/app/_module/constants/leadStatus";
import NoSaleReasonModal from "@/app/(authenticated)/leads/_modules/components/NoSaleReasonModal";
import type { Lead } from "@/app/(authenticated)/leads/_modules/types/get-leads-type";
import { PowerDialerSession } from "@/app/(authenticated)/dialer/_modules/components/PowerDialerSession";
import { MultiLineDialerSession } from "@/app/(authenticated)/dialer/_modules/components/MultiLineDialerSession";
import { QuickNotesPanel } from "@/app/(authenticated)/dialer/_modules/components/QuickNotesPanel";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/app/_module/components/ui/tabs";
import { Switch } from "@/app/_module/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/app/_module/components/ui/popover";
import { endpoint } from "@/app/_module/services/endpoints";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/app/_module/components/ui/tooltip";
import { Label } from "@/app/_module/components/ui/label";
import { createPortal } from "react-dom";
import { ScriptPanel } from "@/app/_module/components/ScriptPanel";
import { useCallScripts } from "@/app/_module/hooks/useCallScripts";
import { useDialBlocker } from "@/app/_module/hooks/useDialBlocker";
import { useDirect130Host } from "@/app/_module/utils/direct130HostContext";
import { NoPhoneNumberDialog } from "@/app/_module/components/NoPhoneNumberDialog";
import { DialBlockerNotice } from "@/app/_module/components/NoActiveNumberNotice";
import { TrialDialTimePill } from "@/app/_module/components/TrialDialTimePill";
import { AgentPhoneSelector } from "@/app/_module/components/AgentPhoneSelector";
import { AudioDeviceSelector } from "@/app/_module/components/AudioDeviceSelector";
import { DialerSessionHistory } from "@/app/_module/components/DialerSessionHistory";
import { QuickDispositionButtons } from "@/app/_module/components/QuickDispositionButtons";
import { useDncConfirm } from "@/app/_module/hooks/useDncConfirm";
import { dialerSessionApi, type DialerSession } from "@/app/_module/services/dialerSessionService";
import { useAutoStatusManager } from "@/app/_module/hooks/useAutoStatusManager";
import {
  RedialThresholdToast,
  type ThresholdEvent,
} from "@/app/(authenticated)/communication/_modules/components/RedialThresholdToast";
import { DIAL_IQ_ENABLED } from "@/app/_module/utils/featureFlags";
import { formatCallTimer } from "@/app/_module/utils/formatters";
import { isLeadOutsideCallingHours } from "@/app/_module/utils/callingHours";

interface InsuraABotKeyCode {
  id: string;
  code: string;
  timestamp: Date;
  duration: number;
  callId: string;
}

const DIALER_MINIMIZED_KEY = 'dialer_widget_minimized';
const DIALER_POSITION_KEY = 'dialer_widget_position';
const AUTO_OPEN_LEAD_DETAILS_KEY = 'dialer_auto_open_lead_details';

// Dial pad digits - defined once outside component for iPad performance (prevents re-creation on every render)
const DIAL_PAD_DIGITS = [
  { digit: "1", letters: "" },
  { digit: "2", letters: "ABC" },
  { digit: "3", letters: "DEF" },
  { digit: "4", letters: "GHI" },
  { digit: "5", letters: "JKL" },
  { digit: "6", letters: "MNO" },
  { digit: "7", letters: "PQRS" },
  { digit: "8", letters: "TUV" },
  { digit: "9", letters: "WXYZ" },
  { digit: "*", letters: "" },
  { digit: "0", letters: "+" },
  { digit: "#", letters: "" },
] as const;


export function UnifiedDialerWidget() {
  const router = useRouter();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Twilio Device context for call state
  const {
    activeCall,
    activeCallSid,
    callMetadata,
    callStatus,
    isDeviceReady,
    deviceError,
    disconnect,
    mute,
    unmute,
    hold,
    unhold,
    isMuted,
    isOnHold,
    callDuration,
    bridgedCalls,
    connect,
    isRecoveringCall,
    sendDigits,
    // Audio device properties
    audioInputDevices,
    audioOutputDevices,
    selectedInputDeviceId,
    selectedOutputDeviceId,
    setAudioInputDevice,
    setAudioOutputDevice,
    refreshAudioDevices,
    microphoneAudioLevel,
    isHardwareMuted,
    // Speaker volume control
    speakerVolume,
    setSpeakerVolume,
    // 3-way calling
    conferenceParticipants,
    isAddingParticipant,
    addParticipantToCall,
    muteParticipant,
    holdParticipant,
    removeParticipant,
    isReconnecting,
    reconnectDevice,
    startAudioMonitoring,
    stopAudioMonitoring,
    lastDialedNumber,
    lastDialedLeadName,
    activeCallSource,
    // Live transfer (2026-07-23)
    incomingTransferOffer,
    acceptTransferOffer,
    declineTransferOffer,
  } = useVoiceDeviceContext();

  // Unified dialer context for lead queue and dialer modes
  const {
    isWidgetOpen,
    isMinimized,
    activeTab,
    selectedLeads,
    dialerSource,
    dialerMode,
    sessionActive,
    openWidget,
    closeWidget,
    minimizeWidget,
    maximizeWidget,
    setActiveTab,
    clearSelectedLeads,
    removeLeadFromDialer,
    startPowerDialer,
    startMultiLineDialer,
    stopDialerSession,
    setInsuraHelpFromDialerOpen,
    isInsuraBotOpen,
    insuraBotRect,
    setInsuraBotOpen,
    isInsuraHelpFromDialerOpen,
    requestDeleteSelectedLeads,
    requestBulkStatusChange,
    manualCallLeadStatus,
    clearManualCallLeadStatus,
    pendingRecovery,
    confirmRecovery,
    declineRecovery,
    currentLeadIndex,
    addLeadsToDialer,
    setCurrentLeadIndex,
    setResumeOffset,
    resumeOffset,
    eavesdropActive: eavesdropSessionActive,
    eavesdropAgentName,
    eavesdropLeadName,
    eavesdropStartedAt,
    eavesdropFollowingAgentId,
    eavesdropMode,
    coachMicActive,
    coachMicVolume,
    coachInboundLevel,
    requestCoachMicStart,
    requestCoachMicStop,
    stopEavesdrop,
  } = useUnifiedDialer();

  // Read the analyser directly for the waveform, and use the provider's latch
  // toggle so the widget behaves exactly like the console's monitor rail.
  const { readSpectrum: coachReadSpectrum, toggleMic: coachToggleMic, micLatched: coachMicLatched } = useCoachSession();

  // Eavesdrop duration tick — forces a re-render every second while listening
  // so the "Listening for Xm Ys" counter in the widget stays live. Without this,
  // the inline Date.now() call only evaluates on unrelated re-renders.
  // Keyed off the raw session flag (not the display-gated `eavesdropActive`
  // derived further down) so the tick is independent of whether this widget is
  // currently the visible surface — and so it stays above that declaration.
  const [, forceEavesdropTick] = useState(0);
  useEffect(() => {
    if (!eavesdropSessionActive) return;
    const id = setInterval(() => forceEavesdropTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [eavesdropSessionActive]);

  // CALL-WAITING: when the agent presses "Hold & Answer" on an incoming call
  // while a dialer session is active, we want to SWAP the widget to the plain
  // Active Call view for the new caller instead of showing a stale dialer
  // session UI behind it. `heldCall` is populated by IncomingCallContext at
  // the moment of the swap; `isMergedCall` covers the 3-way merge path.
  const { heldCall, isMergedCall, coachActive, currentCall: ringingIncomingCall } = useIncomingCall();

  // Plain "Answer" (no Hold/Merge) on an incoming during a dialer session
  // sets the `__callwaiting_active` sessionStorage flag in IncomingCallContext.
  // Read it reactively — without this signal, isInCallWaiting was only true
  // for Hold/Merge cases, leaving plain Answer to fall through to the dialer
  // widget render and hide the new caller's Active Call UI. Mark's repro:
  // "killed testing5656, never showed active ui for incoming."
  const [callWaitingFlag, setCallWaitingFlag] = useState<boolean>(() => {
    try { return typeof window !== 'undefined' && sessionStorage.getItem('__callwaiting_active') === '1'; } catch { return false; }
  });
  useEffect(() => {
    const refresh = () => {
      try { setCallWaitingFlag(sessionStorage.getItem('__callwaiting_active') === '1'); } catch {}
    };
    // Refresh on storage events (cross-tab) and on our custom dispatches
    window.addEventListener('storage', refresh);
    window.addEventListener('multiline:emergency-pause', refresh);
    window.addEventListener('multiline:emergency-resume', refresh);
    window.addEventListener('powerdialer:emergency-pause', refresh);
    window.addEventListener('powerdialer:emergency-resume', refresh);
    // Also poll briefly to catch sessionStorage writes that don't fire events
    const t = setInterval(refresh, 500);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener('multiline:emergency-pause', refresh);
      window.removeEventListener('multiline:emergency-resume', refresh);
      window.removeEventListener('powerdialer:emergency-pause', refresh);
      window.removeEventListener('powerdialer:emergency-resume', refresh);
      clearInterval(t);
    };
  }, []);

  const isInCallWaiting = !!heldCall || isMergedCall || callWaitingFlag;

  // SMS conversation context — hide minimized dialer when both InsuraBot & SMS are open
  const { activeSmsConversation } = useSmsConversation();

  // User context for call forwarding
  const { user } = useUserContext();

  // Auto status manager for setting on_call status during active calls
  const { setOnCallStatus, restoreStatusAfterCall } = useAutoStatusManager();

  // Phone number validation hook
  const { blocked: dialBlocked, blocker: dialBlocker, hasActiveNumbers, isCheckingPhoneNumbers, recheck: recheckPhoneNumbers } = useDialBlocker();

  // Pipeline stages for bulk status change dropdown
  const { data: pipelineStages } = usePipelineStages();
  const bulkUpdateStatus = useBulkUpdateLeadStatus();

  // Track last call time for debouncing
  const lastCallTimeRef = useRef<number>(0);

  // No phone dialog state
  const [showNoPhoneDialog, setShowNoPhoneDialog] = useState(false);

  // Disposition state for manual call widget
  const [showWidgetNoSaleModal, setShowWidgetNoSaleModal] = useState(false);
  const updateLeadStatusMutation = useUpdateLeadStatus();

  // DNC asks first (and offers to block their calls and texts). The call can end — and
  // another start — while that dialog is open, so this tracks the call as it is NOW.
  const { confirmDnc, modal: dncModal } = useDncConfirm();
  const liveCallRef = useRef({ leadId: callMetadata?.leadId, disconnect });
  useEffect(() => {
    liveCallRef.current = { leadId: callMetadata?.leadId, disconnect };
  });

  const handleWidgetDisposition = async (newStatus: string) => {
    if (!leadId) return;
    if (newStatus === LeadStatus.NO_SALE) {
      setShowWidgetNoSaleModal(true);
      return;
    }
    const forLead = leadId;
    let blockInbound = false;
    if (newStatus === LeadStatus.DO_NOT_CALL) {
      const answer = await confirmDnc();
      if (answer === null) return;
      blockInbound = answer;
    }
    try {
      await updateLeadStatusMutation.mutateAsync({ leadId: forLead, status: newStatus, ...(blockInbound ? { blockInbound } : {}) });
      if (liveCallRef.current.leadId === forLead) liveCallRef.current.disconnect();
    } catch {
      toast({ title: "Failed to update status", variant: "destructive" });
    }
  };

  const handleWidgetNoSaleConfirm = async (reason: string) => {
    if (!leadId) return;
    await updateLeadStatusMutation.mutateAsync({
      leadId,
      status: LeadStatus.NO_SALE,
      noSaleReason: reason,
    });
    disconnect();
  };

  // Manual dial state
  const [dialNumber, setDialNumber] = useState("");
  const [isDialing, setIsDialing] = useState(false);

  // Preserve matched lead during active call (since dialNumber gets cleared)
  const [activeCallMatchedLead, setActiveCallMatchedLead] = useState<Lead | null>(null);

  // Power dialer and multi-line always match the caller ID to the lead's area code (owner,
  // 2026-10-09). The manual keypad has its own ON/OFF again (owner, 2026-10-13): OFF dials from
  // the number the agent picks — sent as SelectedCallerId, which only the manual dial sends.
  const aiMatchingEnabled = true;
  const [manualAiMatching, setManualAiMatching] = useState(true);
  const [selectedAgentPhone, setSelectedAgentPhone] = useState<string>("");
  const [matchingStrategy, setMatchingStrategy] = useState<'nearest' | 'metropolitan' | 'exact'>('nearest');

  // Track client mount to avoid hydration mismatches with localStorage/context-derived state
  const [hasMounted, setHasMounted] = useState(false);

  // Sync the matching strategy and the manual ON/OFF from localStorage after hydration (avoids SSR/client mismatch)
  useEffect(() => {
    setHasMounted(true);
    if (localStorage.getItem('dialerAiMatchingEnabled') === 'false') setManualAiMatching(false);
    const savedStrategy = localStorage.getItem('dialerMatchingStrategy');
    if (savedStrategy === 'metropolitan' || savedStrategy === 'exact' || savedStrategy === 'nearest') {
      setMatchingStrategy(savedStrategy);
    }
  }, []);

  // Sync strategy when settings page changes it (same-tab custom event)
  useEffect(() => {
    const handler = (e: Event) => {
      const strategy = (e as CustomEvent<{ strategy: string }>).detail?.strategy;
      if (strategy === 'metropolitan' || strategy === 'exact' || strategy === 'nearest') {
        setMatchingStrategy(strategy);
      }
    };
    window.addEventListener('dialerStrategyChanged', handler);
    return () => window.removeEventListener('dialerStrategyChanged', handler);
  }, []);

  const [isSavingStrategy, setIsSavingStrategy] = useState(false);

  // AI Area Code popover — controlled open state + recommendations
  const [isAiPopoverOpen, setIsAiPopoverOpen] = useState(false);
  const [popoverView, setPopoverView] = useState<'strategy' | 'recommendations'>('strategy');
  const [popoverRecs, setPopoverRecs] = useState<Array<{areaCode: string; state: string; city?: string; priority: string; estimatedCost: number; potentialMatches?: number; reason?: string}>>([]);
  const [isLoadingRecs, setIsLoadingRecs] = useState(false);

  const fetchPopoverRecs = useCallback(async () => {
    setIsLoadingRecs(true);
    try {
      const result = await handleApiCall(async () => api.get({
        url: getApiUrl(endpoint.communication.areaCodePurchaseRecommendations),
        auth: true,
      }));
      // result = { recommendations: [...], totalRecommendations, highPriority, ... }
      if (result?.recommendations && Array.isArray(result.recommendations)) {
        setPopoverRecs(result.recommendations);
      }
    } catch {
      // silently fail — recommendations are non-critical
    } finally {
      setIsLoadingRecs(false);
    }
  }, []);

  const handleAiPopoverOpenChange = useCallback((open: boolean) => {
    setIsAiPopoverOpen(open);
    if (open) {
      setPopoverView('strategy');
      setInsuraBotOpen(false);
      setIsInsuraHelpOpenLocal(false);
      fetchPopoverRecs();
    }
  }, [setInsuraBotOpen, fetchPopoverRecs]);

  // Auto-close AI popover when InsuraBot opens
  useEffect(() => {
    if (isInsuraBotOpen && isAiPopoverOpen) {
      setIsAiPopoverOpen(false);
    }
  }, [isInsuraBotOpen]);

  const handleWidgetStrategyToggle = async (strategy: 'nearest' | 'metropolitan' | 'exact') => {
    try {
      setIsSavingStrategy(true);
      await handleApiCall(async () => api.put({
        url: getApiUrl(endpoint.areaCodePreferences.updateMatchingStrategy),
        body: { strategy },
        auth: true,
      }));
      setMatchingStrategy(strategy);
      localStorage.setItem('dialerMatchingStrategy', strategy);
    } catch {
      toast({ title: "Update Failed", description: "Could not save strategy.", variant: "destructive" });
    } finally {
      setIsSavingStrategy(false);
    }
  };

  // Current view: 'keypad', 'queue', 'powerdial', 'multiline'
  const [currentView, setCurrentView] = useState<'keypad' | 'queue' | 'powerdial' | 'multiline' | 'history'>('keypad');

  // InsuraBot state
  const [insuraABotKeyCodes, setInsuraABotKeyCodes] = useState<InsuraABotKeyCode[]>([]);
  const [isDTMFListening, setIsDTMFListening] = useState(false);
  const [isSpeechProcessing, setIsSpeechProcessing] = useState(false);
  const dtmfPollingRef = useRef<DTMFPollingWrapperRef>(null);

  // Tab state for active call view
  const [callTab, setCallTab] = useState<'call' | 'insurabot'>('call');

  // Dial pad state for active call
  const [showActiveCallDialPad, setShowActiveCallDialPad] = useState(false);

  // Add party panel state (button-triggered, not a tab)
  const [showAddPartyPanel, setShowAddPartyPanel] = useState(false);

  // Quick Notes panel state
  const [showQuickNotes, setShowQuickNotes] = useState(false);

  // Outbound caller ID toggle state
  const [showCallerId, setShowCallerId] = useState(true);

  // Add participant state (inline, no modal)
  const [addPartyNumber, setAddPartyNumber] = useState("");
  const [showParticipantsPanel, setShowParticipantsPanel] = useState(false);

  // Unread count for minimized state
  const [unreadCount, setUnreadCount] = useState(0);

  // Session completion state
  const [showSessionComplete, setShowSessionComplete] = useState(false);
  const [sessionWasStopped, setSessionWasStopped] = useState(false);
  // Ref for auto-resume — handleResumeSession is defined later, so we use a ref
  const handleResumeSessionRef = useRef<((session: any) => void) | null>(null);
  const [sessionStats, setSessionStats] = useState({
    answered: 0,
    called: 0,
    totalDialTime: 0,
    talkSeconds: 0,
    conversationSeconds: 0,
    total: 0,
    appointments: 0,
    underwriting: 0,
    sold: 0,
  });

  // Manual call tracking - for showing stats after call ends
  const [manualCallAnswered, setManualCallAnswered] = useState(false);
  const manualCallStartTimeRef = useRef<number | null>(null);

  // InsuraHelp panel state (attached to dialer)
  const [isInsuraHelpOpen, setIsInsuraHelpOpenLocal] = useState(false);

  // Script panel state
  const [isScriptPanelOpen, setIsScriptPanelOpen] = useState(false);
  const [selectedScriptId, setSelectedScriptId] = useState<string | null>(null);
  const { data: scripts = [] } = useCallScripts();

  // Detect currently viewed lead from URL (e.g. /leads/[id])
  const pathname = usePathname();

  // DON'T DOUBLE UP ON THE MONITORING UI (2026-07-24). The Live Calls console
  // has its own sticky monitor rail with the same controls (volume, push-to-talk,
  // stop), so while the supervisor is ON that page this floating widget was a
  // second, redundant surface for the exact same session — two timers, two Stop
  // buttons, two PTT buttons for one call.
  //
  // The widget still earns its place the moment they navigate AWAY: the session
  // keeps running and this becomes the only way to control it. So suppress ONLY
  // the eavesdrop presentation, ONLY on the agency dashboard. Every other widget
  // mode (live call, dialer session, InsuraBot) is untouched.
  // /m/agency-dashboard is the SAME console — the old check missed the /m
  // prefix, so the eavesdrop chrome rendered on mobile in the one place it was
  // designed not to.
  const onLiveCallsConsole =
    !!pathname?.startsWith("/agency-dashboard") || !!pathname?.startsWith("/m/agency-dashboard");
  const [speakerOn, setSpeakerOn] = useState(false);
  /** Native app / mobile routes: the widget is a full sheet, not a floating panel. */
  const isMobileShell = !!pathname?.startsWith("/m");
  const eavesdropActive = eavesdropSessionActive && !onLiveCallsConsole;

  const viewedLeadId = useMemo(() => {
    const match = pathname?.match(/\/leads\/([a-f0-9-]{36})/);
    return match?.[1] || null;
  }, [pathname]);
  const { data: viewedLeadResponse } = useLead(viewedLeadId || "");
  const viewedLead = useMemo(() => {
    if (!viewedLeadResponse) return null;
    if (typeof viewedLeadResponse === "object" && "data" in viewedLeadResponse && viewedLeadResponse.data) {
      return viewedLeadResponse.data as Lead;
    }
    if (typeof viewedLeadResponse === "object" && "name" in viewedLeadResponse) {
      return viewedLeadResponse as unknown as Lead;
    }
    return null;
  }, [viewedLeadResponse]);

  // Auto-select default script when scripts first load
  useEffect(() => {
    if (scripts.length > 0 && !selectedScriptId) {
      const defaultScript = scripts.find((s) => s.isDefault);
      if (defaultScript) setSelectedScriptId(defaultScript.id);
    }
  }, [scripts, selectedScriptId]);

  // Auto-open lead details toggle - start with false to avoid hydration mismatch
  const [autoOpenLeadDetails, setAutoOpenLeadDetails] = useState(false);
  const [autoOpenLeadDetailsHydrated, setAutoOpenLeadDetailsHydrated] = useState(false);

  // Load auto-open setting from localStorage after hydration
  useEffect(() => {
    try {
      const saved = localStorage.getItem(AUTO_OPEN_LEAD_DETAILS_KEY);
      if (saved === 'true') {
        setAutoOpenLeadDetails(true);
      }
    } catch {
      // Ignore localStorage errors
    }
    setAutoOpenLeadDetailsHydrated(true);
  }, []);

  // Persist auto-open lead details setting after hydration
  useEffect(() => {
    if (autoOpenLeadDetailsHydrated) {
      try {
        localStorage.setItem(AUTO_OPEN_LEAD_DETAILS_KEY, autoOpenLeadDetails.toString());
      } catch {
        // Ignore localStorage errors
      }
    }
  }, [autoOpenLeadDetails, autoOpenLeadDetailsHydrated]);

  // Sync InsuraHelp state with context so InsuraBot can respond
  const setIsInsuraHelpOpen = useCallback((open: boolean) => {
    setIsInsuraHelpOpenLocal(open);
    setInsuraHelpFromDialerOpen(open);
  }, [setInsuraHelpFromDialerOpen]);

  // Sync local InsuraHelp state when context changes.
  // CLOSE sync: e.g., when InsuraBot closes the panel.
  // OPEN sync: e.g., when InsuraBot's "?" button asks the dialer's
  // InsuraHelp panel to open (so the agent can hand off from chat to help).
  useEffect(() => {
    if (isInsuraHelpFromDialerOpen && !isInsuraHelpOpen) {
      setIsInsuraHelpOpenLocal(true);
    } else if (!isInsuraHelpFromDialerOpen && isInsuraHelpOpen) {
      setIsInsuraHelpOpenLocal(false);
    }
  }, [isInsuraHelpFromDialerOpen, isInsuraHelpOpen]);

  // Widget position: 'left' (next to sidebar) or 'right' (bottom-right corner)
  // Start with 'right' default to avoid hydration mismatch
  const [widgetPosition, setWidgetPosition] = useState<'left' | 'right'>('right');
  const [widgetPositionHydrated, setWidgetPositionHydrated] = useState(false);

  // Load widget position from localStorage after hydration
  useEffect(() => {
    try {
      const saved = localStorage.getItem(DIALER_POSITION_KEY);
      if (saved === 'left') {
        setWidgetPosition('left');
      }
    } catch {
      // Ignore localStorage errors
    }
    setWidgetPositionHydrated(true);
  }, []);

  // Input ref for focus
  const inputRef = useRef<HTMLInputElement>(null);

  // Floating call bar drag refs (same pattern as ScriptPanel)
  const floatingBarRef = useRef<HTMLDivElement>(null);
  const floatingBarDragging = useRef(false);
  const floatingBarOrigin = useRef({ mx: 0, my: 0, x: 0, y: 0 });
  const floatingBarPos = useRef<{ x: number; y: number } | null>(null);
  const floatingBarRafId = useRef(0);

  const applyFloatingBarPos = useCallback(() => {
    if (floatingBarRef.current && floatingBarPos.current) {
      floatingBarRef.current.style.transform = `translate3d(${floatingBarPos.current.x}px, ${floatingBarPos.current.y}px, 0)`;
    }
  }, []);

  // Floating bar drag listeners
  useEffect(() => {
    function onMove(e: MouseEvent) {
      if (!floatingBarDragging.current) return;
      cancelAnimationFrame(floatingBarRafId.current);
      floatingBarRafId.current = requestAnimationFrame(() => {
        if (!floatingBarPos.current) return;
        const barWidth = floatingBarRef.current?.offsetWidth || 300;
        const barHeight = floatingBarRef.current?.offsetHeight || 48;
        floatingBarPos.current.x = Math.min(Math.max(
          floatingBarOrigin.current.x + (e.clientX - floatingBarOrigin.current.mx),
          -window.innerWidth + barWidth + 32
        ), 0);
        floatingBarPos.current.y = Math.min(Math.max(
          floatingBarOrigin.current.y + (e.clientY - floatingBarOrigin.current.my),
          -window.innerHeight + barHeight + 32
        ), 0);
        applyFloatingBarPos();
      });
    }
    function onUp() {
      if (floatingBarDragging.current) {
        floatingBarDragging.current = false;
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
      }
    }
    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      cancelAnimationFrame(floatingBarRafId.current);
    };
  }, [applyFloatingBarPos]);

  const onFloatingBarDragStart = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest("button")) return;
    e.preventDefault();
    if (!floatingBarPos.current) floatingBarPos.current = { x: 0, y: 0 };
    floatingBarDragging.current = true;
    floatingBarOrigin.current = { mx: e.clientX, my: e.clientY, x: floatingBarPos.current.x, y: floatingBarPos.current.y };
    document.body.style.userSelect = "none";
    document.body.style.cursor = "grabbing";
  }, []);

  // Reset floating bar position when widget is maximized (so it returns to default on next minimize)
  useEffect(() => {
    if (!isMinimized) {
      floatingBarPos.current = null;
      if (floatingBarRef.current) {
        floatingBarRef.current.style.transform = "";
      }
    }
  }, [isMinimized]);

  // Persist position to localStorage after hydration
  useEffect(() => {
    if (widgetPositionHydrated) {
      try {
        localStorage.setItem(DIALER_POSITION_KEY, widgetPosition);
      } catch {
        // Ignore localStorage errors
      }
    }
  }, [widgetPosition, widgetPositionHydrated]);

  // Get the first active bridged call (for phone forwarding mode) - memoized for iPad performance
  const activeBridgedCall = useMemo(() =>
    bridgedCalls.size > 0 ? Array.from(bridgedCalls.values())[0] : null,
    [bridgedCalls]
  );
  const isBridgedCall = !activeCall && !!activeBridgedCall;
  const hasActiveCall = !!(activeCall && activeCallSid) || isBridgedCall;

  // Look up lead by phone number (for main keypad) - memoized for iPad performance
  const cleanedDialNumber = useMemo(() => dialNumber.replace(/\D/g, ""), [dialNumber]);
  const { data: matchedLead } = useLeadByPhone(
    cleanedDialNumber.length === 10 ? cleanedDialNumber : ""
  );

  // Create lead mutation (for adding leads from dialer)
  const createLeadMutation = useCreateLead();
  const [isCreatingLead, setIsCreatingLead] = useState(false);
  const [showAddLeadForm, setShowAddLeadForm] = useState(false);
  const [newLeadName, setNewLeadName] = useState("");

  const handleCreateLeadFromDialer = useCallback(async () => {
    if (cleanedDialNumber.length !== 10) return;
    if (!newLeadName.trim() || newLeadName.trim().length < 2) {
      toast({
        title: "Name required",
        description: "Please enter a name (at least 2 characters).",
        variant: "destructive",
      });
      return;
    }

    setIsCreatingLead(true);
    try {
      const result = await createLeadMutation.mutateAsync({
        name: newLeadName.trim(),
        phone: cleanedDialNumber,
      });

      // Extract lead ID from response
      const leadId = (result as any)?.data?.data?.lead?.id ||
                     (result as any)?.data?.lead?.id ||
                     (result as any)?.lead?.id;

      // Invalidate the phone lookup query so the dialer shows the new lead
      await queryClient.invalidateQueries({
        queryKey: leadsQueryKeys.byPhone(cleanedDialNumber)
      });

      if (leadId) {
        toast({
          title: "Lead created",
          description: `${newLeadName.trim()} created. Opening lead details...`,
        });
        // Reset form state
        setShowAddLeadForm(false);
        setNewLeadName("");
        // Navigate to the new lead (shell stays under /m and tucks the dialer)
        if (isMobileShell) {
          minimizeWidget();
          router.push(`/m/leads/${leadId}`);
        } else {
          router.push(`/leads/${leadId}`);
        }
      } else {
        toast({
          title: "Lead created",
          description: "New lead created successfully.",
        });
        setShowAddLeadForm(false);
        setNewLeadName("");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create lead. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCreatingLead(false);
    }
  }, [cleanedDialNumber, newLeadName, createLeadMutation, toast, router, queryClient]);

  // Reset add lead form when dial number changes
  useEffect(() => {
    setShowAddLeadForm(false);
    setNewLeadName("");
  }, [cleanedDialNumber]);

  // State for adding lead during active call (for incoming unknown numbers)
  const [showActiveCallAddLeadForm, setShowActiveCallAddLeadForm] = useState(false);
  const [activeCallNewLeadName, setActiveCallNewLeadName] = useState("");

  // Look up lead by phone number (for add party) - memoized for iPad performance
  const cleanedAddPartyNumber = useMemo(() => addPartyNumber.replace(/\D/g, ""), [addPartyNumber]);
  const { data: addPartyMatchedLead } = useLeadByPhone(
    cleanedAddPartyNumber.length === 10 ? cleanedAddPartyNumber : ""
  );

  // Auto-switch to queue view when leads are selected
  // Also clear session complete state so it doesn't persist after selecting new leads
  useEffect(() => {
    if (selectedLeads.length > 0 && !sessionActive && currentView === 'keypad') {
      setCurrentView('queue');
    }
    // Clear session complete when new leads are selected (fixes bug where "Session Ended" persists after minimizing and selecting new leads)
    if (selectedLeads.length > 0 && showSessionComplete) {
      setShowSessionComplete(false);
      setSessionStats({ answered: 0, called: 0, totalDialTime: 0, talkSeconds: 0, conversationSeconds: 0, total: 0, appointments: 0, underwriting: 0, sold: 0 });
    }
  }, [selectedLeads.length, sessionActive, currentView, showSessionComplete]);

  // Pre-dial TCPA calling-hours check — how many SELECTED leads are currently
  // outside 8am–9pm in THEIR local time (would be skipped on power/multi-line).
  // Shown as a banner so the agent knows before starting. Re-checks on selection
  // change and every 5 min (a lead at 8:59pm becomes blocked at 9pm).
  const [outsideHoursCount, setOutsideHoursCount] = useState<number>(0);
  const [blockedLeadIds, setBlockedLeadIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    const ids = (selectedLeads as any[]).map((l) => l?.id).filter(Boolean);
    if (ids.length === 0) {
      setOutsideHoursCount(0);
      setBlockedLeadIds(new Set());
      return;
    }
    // INSTANT: flag after-hours leads client-side from their state (no wait).
    // The debounced backend check below then reconciles edge cases.
    const instant = new Set(
      (selectedLeads as any[])
        .filter((l) => l && isLeadOutsideCallingHours(l))
        .map((l) => String(l.id)),
    );
    setBlockedLeadIds(instant);
    setOutsideHoursCount(instant.size);
    let cancelled = false;
    const runCheck = async () => {
      try {
        const res: any = await api.post({
          url: getApiUrl('/communication/calling-hours/check'),
          body: { leadIds: ids },
          auth: true,
        });
        const blocked = res?.data?.blocked ?? res?.blocked ?? 0;
        const blockedIds: string[] = res?.data?.blockedLeadIds ?? res?.blockedLeadIds ?? [];
        if (!cancelled) {
          setOutsideHoursCount(Number(blocked) || 0);
          setBlockedLeadIds(new Set(blockedIds.map((x) => String(x))));
        }
      } catch {
        /* non-fatal — enforcement still happens at dial time */
      }
    };
    runCheck(); // reconcile immediately; instant client flag already painted
    const interval = setInterval(runCheck, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLeads]);

  // Switch back to keypad when all leads are deselected
  useEffect(() => {
    if (selectedLeads.length === 0 && currentView === 'queue') {
      setCurrentView('keypad');
    }
  }, [selectedLeads.length, currentView]);

  // Auto-switch to powerdial/multiline view when session starts
  useEffect(() => {
    if (sessionActive && dialerMode === 'power') {
      setCurrentView('powerdial');
    } else if (sessionActive && dialerMode === 'multiline') {
      setCurrentView('multiline');
    }
  }, [sessionActive, dialerMode]);

  // Reset view when session ends
  useEffect(() => {
    if (!sessionActive && (currentView === 'powerdial' || currentView === 'multiline')) {
      setCurrentView(selectedLeads.length > 0 ? 'queue' : 'keypad');
    }
  }, [sessionActive, currentView, selectedLeads.length]);

  // Track unread messages when minimized
  useEffect(() => {
    if (isMinimized && hasActiveCall) {
      setUnreadCount(prev => prev + 1);
    }
  }, [hasActiveCall, isMinimized]);

  // Clear unread count when maximized
  useEffect(() => {
    if (!isMinimized) {
      setUnreadCount(0);
    }
  }, [isMinimized]);

  // Auto-maximize when an active call or dialer session starts (user can still minimize during)
  const prevHasActiveCallRef = useRef(false);
  const prevSessionActiveRef = useRef(false);
  useEffect(() => {
    // Only auto-maximize on a FRESH active call. For multi-line we gate on
    // callStatus === in-progress so the widget doesn't pop open while lines
    // are still ringing (that would leak the lead name of the first-dialed
    // line before anyone actually picked up). For manual calls from lead
    // details (e.g., "Call Caius"), we open immediately on ringing — user
    // expects the dialer to appear right when they click call.
    const justBecameActive = hasActiveCall && !prevHasActiveCallRef.current;
    const isMultiLine = dialerMode === 'multiline';
    const isConnected = callStatus === 'in-progress' || callStatus === 'answered';
    const shouldOpen = isMultiLine ? isConnected : true;
    if (justBecameActive && shouldOpen && isMinimized) {
      maximizeWidget();
    }
    prevHasActiveCallRef.current = hasActiveCall;
  }, [hasActiveCall, isMinimized, maximizeWidget, callStatus, dialerMode]);

  // HAND-OFF FROM THE LIVE CALLS CONSOLE (2026-07-25).
  // While the supervisor is on /agency-dashboard the console's own monitor rail
  // is the control surface and this widget stays out of the way. The instant
  // they navigate AWAY with a session still running, this widget becomes the
  // ONLY way to hear/stop it — so pop it open rather than leaving them to spot
  // a small pill. Fires only on the transition (console → elsewhere), so it
  // never fights a manual minimise afterwards.
  const wasOnLiveCallsConsoleRef = useRef(onLiveCallsConsole);
  useEffect(() => {
    const left = wasOnLiveCallsConsoleRef.current && !onLiveCallsConsole;
    wasOnLiveCallsConsoleRef.current = onLiveCallsConsole;
    if (left && eavesdropSessionActive && isMinimized) {
      maximizeWidget();
    }
  }, [onLiveCallsConsole, eavesdropSessionActive, isMinimized, maximizeWidget]);

  useEffect(() => {
    // Session starting ≠ call connected. For multi-line, the session starts the
    // moment lines begin dialing — we don't want to pop the widget open then.
    // Skip auto-open for multi-line; it'll open via the hasActiveCall+connected
    // effect above when a lead actually picks up.
    if (dialerMode === 'multiline') {
      prevSessionActiveRef.current = sessionActive;
      return;
    }
    if (sessionActive && !prevSessionActiveRef.current && isMinimized) {
      maximizeWidget();
    }
    prevSessionActiveRef.current = sessionActive;
  }, [sessionActive, isMinimized, maximizeWidget, dialerMode]);

  // Track previous active call state for status management
  const wasOnCallRef = useRef(false);

  // Auto-set user status to "on_call" when active call starts, restore when call ends
  // For power/multiline dialer sessions, keep on_call status for the entire session
  // (don't toggle between calls — prevents idle/logout timers from restarting between leads)
  useEffect(() => {
    const isInDialerSession = sessionActive && (dialerMode === 'power' || dialerMode === 'multiline');

    if ((hasActiveCall || isInDialerSession) && !wasOnCallRef.current) {
      // Call or dialer session just started - set status to on_call
      wasOnCallRef.current = true;
      setOnCallStatus();
    } else if (!hasActiveCall && !isInDialerSession && wasOnCallRef.current) {
      // Call ended AND no active dialer session - restore previous status
      wasOnCallRef.current = false;
      restoreStatusAfterCall();
    }
  }, [hasActiveCall, sessionActive, dialerMode, setOnCallStatus, restoreStatusAfterCall]);

  // Track manual call answered state (for showing session complete stats after manual calls)
  // Only tracks when not in a dialer session (power/multiline)
  useEffect(() => {
    if (!sessionActive && hasActiveCall && !manualCallStartTimeRef.current) {
      // Manual call just started - clear any previous session complete screen
      manualCallStartTimeRef.current = Date.now();
      setManualCallAnswered(false);
      if (showSessionComplete) {
        setShowSessionComplete(false);
      }
    }
  }, [sessionActive, hasActiveCall, showSessionComplete]);

  // Track when manual call is answered (callStatus becomes 'in-progress' or 'answered')
  useEffect(() => {
    if (!sessionActive && hasActiveCall && !manualCallAnswered) {
      if (callStatus === 'in-progress' || callStatus === 'answered') {
        setManualCallAnswered(true);
      }
    }
  }, [sessionActive, hasActiveCall, callStatus, manualCallAnswered]);

  // Show session complete for manual calls when call ends
  useEffect(() => {
    if (!sessionActive && !hasActiveCall && manualCallStartTimeRef.current) {
      // Manual call just ended - show stats
      const totalDialTime = Math.floor((Date.now() - manualCallStartTimeRef.current) / 1000);

      // Calculate outcome stats from tracked lead status
      let appointments = 0;
      let underwriting = 0;
      let sold = 0;
      if (manualCallLeadStatus) {
        if (manualCallLeadStatus === 'Appt' || manualCallLeadStatus === 'Appointment') {
          appointments = 1;
        } else if (manualCallLeadStatus === 'Underwriting' || manualCallLeadStatus === 'UWR') {
          underwriting = 1;
        } else if (manualCallLeadStatus === 'Sold & Approved' || manualCallLeadStatus === 'Approved') {
          sold = 1;
        }
      }

      setSessionStats({
        answered: manualCallAnswered ? 1 : 0,
        called: 1,
        totalDialTime,
        // A manual call has no dialing phase — the session IS the call, so
        // talk time equals dial time. Same 60s rule for "real conversation".
        talkSeconds: manualCallAnswered ? totalDialTime : 0,
        conversationSeconds: manualCallAnswered && totalDialTime >= 60 ? totalDialTime : 0,
        total: 1,
        appointments,
        underwriting,
        sold,
      });
      setShowSessionComplete(true);

      // Reset tracking
      manualCallStartTimeRef.current = null;
      setManualCallAnswered(false);
      clearManualCallLeadStatus();
      setActiveCallMatchedLead(null); // Clear preserved lead when call ends

      // AUTO-RESUME: If the multi-line session was auto-stopped for this manual call,
      // fetch the latest stopped session from history and resume it automatically.
      if (wasManualCallAutoStopped()) {
        clearManualCallAutoStopped();
        setShowSessionComplete(false); // Don't show manual call stats
        setTimeout(async () => {
          try {
            const result = await dialerSessionApi.listSessions(1, 5) as any;
            const sessions = result?.data?.data?.sessions || result?.data?.sessions || [];
            const resumable = sessions.find(
              (s: any) => (s.status === 'stopped' || s.status === 'paused') &&
                s.currentLeadIndex < s.totalLeadsToCall &&
                s.leadQueue && s.leadQueue.length > 0
            );
            if (resumable) {
              handleResumeSessionRef.current?.(resumable);
            }
          } catch (e) {
            console.error('Auto-resume failed:', e);
          }
        }, 500);
      }
    }
  }, [sessionActive, hasActiveCall, manualCallAnswered, manualCallLeadStatus, clearManualCallLeadStatus]);

  // Format phone number for display
  const formatPhone = (phone: string): string => {
    if (!phone) return "";
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    if (cleaned.length === 11 && cleaned.startsWith("1")) {
      return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
  };

  // Format call duration
  const formatDuration = (seconds: number) => formatCallTimer(seconds);

  // Get status display info
  const getStatusColor = () => {
    switch (callStatus) {
      case "queued":
      case "initiated":
        return "bg-yellow-100 text-yellow-800";
      case "ringing":
        return "bg-blue-100 text-blue-800";
      case "answered":
      case "in-progress":
        return "bg-green-100 text-green-800";
      case "completed":
        return "bg-gray-100 text-gray-800";
      case "failed":
      case "busy":
        return "bg-red-100 text-red-800";
      case "no-answer":
      case "no_answer":
        return "bg-orange-100 text-orange-800";
      case "voicemail":
        return "bg-purple-100 text-purple-800";
      case "canceled":
        return "bg-gray-100 text-gray-600";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusText = () => {
    if (isBridgedCall) {
      const status = activeBridgedCall?.status;
      switch (status) {
        case "queued":
        case "initiated":
        case "ringing":
          return "Calling your phone...";
        case "answered":
          return "Connecting to lead...";
        case "in-progress":
          return "Call connected via phone";
        case "completed":
          return "Call Ended";
        case "failed":
          return "Call Failed";
        case "busy":
          return "Busy";
        case "no-answer":
        case "no_answer":
          return "No Answer";
        case "voicemail":
          return "Voicemail";
        case "canceled":
          return "Canceled";
        default:
          return "Connecting...";
      }
    }

    switch (callStatus) {
      case "queued":
        return "Queued";
      case "initiated":
        return "Initiating...";
      case "ringing":
        return "Ringing...";
      case "answered":
      case "in-progress":
        return "Answered";
      case "completed":
        return "Call Ended";
      case "failed":
        return "Call Failed";
      case "busy":
        return "Busy";
      case "no-answer":
      case "no_answer":
        return "No Answer";
      case "voicemail":
        return "Voicemail";
      case "canceled":
        return "Canceled";
      default:
        return "Connecting...";
    }
  };

  // Handle DTMF updates from polling wrapper
  const handleDTMFUpdate = useCallback((digits: DTMFDigit[]) => {
    if (!activeCallSid) return;

    const convertedCodes: InsuraABotKeyCode[] = digits.map((digit, digitIndex) => ({
      // Index is the tiebreaker: timestamp.toString() is only SECOND-resolution, so
      // two digits pressed inside the same second produced identical React keys
      // ("Encountered two children with the same key"). The digit list is
      // append-only, so the index is stable across renders.
      id: `${activeCallSid}-${digit.timestamp.toString()}-${digitIndex}`,
      code: digit.digit,
      timestamp: new Date(digit.timestamp),
      duration: digit.callDuration || 0,
      callId: activeCallSid,
    }));

    setInsuraABotKeyCodes(convertedCodes);
  }, [activeCallSid]);

  // Clear InsuraBot codes
  const handleClearCodes = useCallback(() => {
    setInsuraABotKeyCodes([]);
    dtmfPollingRef.current?.clearDigits();
  }, []);

  // Format digits as (XXX) XXX-XXXX for display
  const formatDialDisplay = (digits: string): string => {
    const cleaned = digits.replace(/\D/g, "");
    if (cleaned.length === 0) return "";
    if (cleaned.length <= 3) return `(${cleaned}`;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  // Handle phone input change (typing or paste)
  const handlePhoneInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const digits = value.replace(/\D/g, "").slice(0, 10);
    setDialNumber(digits);
  }, []);

  // Handle manual dial number pad input
  const handleDialPadPress = useCallback((digit: string) => {
    if (cleanedDialNumber.length < 10) {
      setDialNumber(prev => prev + digit);
    }
  }, [cleanedDialNumber.length]);

  // Handle manual dial backspace
  const handleDialBackspace = useCallback(() => {
    setDialNumber(prev => prev.slice(0, -1));
  }, []);

  // Global keyboard listener for manual dial - allows typing numbers without focusing input
  useEffect(() => {
    // Only active when on call tab, idle mode (manual dial), and widget is expanded
    if (activeTab !== 'call' || dialerMode !== 'idle' || isMinimized) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Only capture if no other input/textarea is focused
      const activeElement = document.activeElement;
      const isInputFocused = activeElement?.tagName === 'INPUT' ||
                            activeElement?.tagName === 'TEXTAREA' ||
                            activeElement?.getAttribute('contenteditable') === 'true';

      // If an input is focused, let it handle the keypress naturally
      if (isInputFocused) return;

      // Handle number keys (0-9) and special dialpad keys (* and #)
      if (/^[0-9]$/.test(e.key)) {
        e.preventDefault();
        handleDialPadPress(e.key);
      } else if (e.key === '*' || e.key === '#') {
        e.preventDefault();
        handleDialPadPress(e.key);
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        handleDialBackspace();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeTab, dialerMode, isMinimized, handleDialPadPress, handleDialBackspace]);

  // Global keyboard listener for active call dialpad - send DTMF tones
  useEffect(() => {
    // Only active when dialpad is open during active call and widget is expanded
    if (!showActiveCallDialPad || !activeCallSid || isMinimized || isBridgedCall) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const activeElement = document.activeElement;
      const isInputFocused = activeElement?.tagName === 'INPUT' ||
                            activeElement?.tagName === 'TEXTAREA' ||
                            activeElement?.getAttribute('contenteditable') === 'true';

      if (isInputFocused) return;

      // Handle number keys (0-9) and special dialpad keys (* and #)
      if (/^[0-9]$/.test(e.key) || e.key === '*' || e.key === '#') {
        e.preventDefault();
        sendDigits?.(e.key);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showActiveCallDialPad, activeCallSid, isMinimized, isBridgedCall, sendDigits]);

  // Handle manual dial call
  const handleManualCall = useCallback(async () => {
    // Check if user has active phone numbers before allowing calls
    if (dialBlocked && dialBlocker) {
      setShowNoPhoneDialog(true);
      toast({ title: dialBlocker.title, description: dialBlocker.body, variant: "destructive" });
      return;
    }

    // Debounce: Prevent duplicate calls within 2 seconds
    const now = Date.now();
    if (now - lastCallTimeRef.current < 2000) {
      toast({
        title: "Please Wait",
        description: "Already initiating a call. Please wait a moment.",
        variant: "destructive",
      });
      return;
    }
    lastCallTimeRef.current = now;

    // Validate phone number is 10-digit US format
    if (cleanedDialNumber.length !== 10) {
      toast({
        title: "Invalid Phone Number",
        description: "Please enter a valid 10-digit US phone number.",
        variant: "destructive",
      });
      return;
    }

    // Call forwarding is ONLY for inbound calls (simultaneous ring)
    // For outbound calls from Manual Dialer, ALWAYS use browser calling

    // Use browser calling (modern method)
    if (!isDeviceReady) {
      toast({
        title: "Device Not Ready",
        description: "Calling device is not ready. Please refresh the page or enable call forwarding in Settings.",
        variant: "destructive",
      });
      return;
    }

    setIsDialing(true);

    try {
      // Request microphone permission before starting call
      const { requestMicrophoneWithGuidance } = await import("@/app/_module/utils/microphone-permission");
      const permissionResult = await requestMicrophoneWithGuidance();

      if (!permissionResult.granted) {
        toast({
          title: "Microphone Access Required",
          description: permissionResult.error || "Please allow microphone access to make calls.",
          variant: "destructive",
        });
        setIsDialing(false);
        return;
      }

      // Create session record for manual dialer
      try {
        await api.post({
          url: getApiUrl('/communication/sessions/manual-call'),
          body: {
            toNumber: cleanedDialNumber,
            leadId: matchedLead?.id || undefined,
          },
          auth: true,
        });
      } catch (sessionError: unknown) {
        const errorMessage = sessionError instanceof Error ? sessionError.message : "Failed to create call session";
        // The server's trial-cap 403 arrives here as errorMessage. Name it for
        // what it is instead of "Session Error" so the agent knows it's a plan
        // limit, not a glitch to retry.
        const isCap = /limit/i.test(errorMessage);
        toast({
          title: isCap ? "Trial limit reached" : "Session Error",
          description: errorMessage,
          variant: "destructive",
          duration: isCap ? 10000 : undefined,
        });
        setIsDialing(false);
        return;
      }

      // Use Twilio Voice SDK browser calling
      const formattedNumber = "+1" + cleanedDialNumber;
      const call = await connect({
        to: formattedNumber,
        from: manualAiMatching ? undefined : (selectedAgentPhone || undefined),
        leadId: matchedLead?.id,
        metadata: {
          source: "unified_dialer",
          phoneNumber: formattedNumber,
          leadName: matchedLead?.name || "Unknown",
        },
        forceShowWidget: true,
      });

      if (call) {
        setInsuraABotKeyCodes([]);
        toast({
          title: "Call Connected",
          description: `Connected to ${formatPhoneNumber(dialNumber)}`,
        });

        // Preserve the matched lead for display during active call BEFORE clearing dial number
        if (matchedLead) {
          setActiveCallMatchedLead(matchedLead);
        }

        setDialNumber("");

        // Auto-open lead details if enabled and lead was matched
        if (autoOpenLeadDetails && matchedLead?.id) {
          if (isMobileShell) {
            // Phone: tuck the dialer away (call stays live, pill shows) and
            // land on the shell's lead page so the agent sees who they're
            // talking to.
            minimizeWidget();
            router.push(`/m/leads/${matchedLead.id}`);
          } else {
            router.push(`/leads/${matchedLead.id}`);
          }
        }
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to initiate call";
      toast({
        title: "Call Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsDialing(false);
    }
  }, [cleanedDialNumber, dialNumber, isDeviceReady, connect, matchedLead, toast, hasActiveNumbers, user, manualAiMatching, selectedAgentPhone, autoOpenLeadDetails, router, setActiveCallMatchedLead]);

  // Start power dialer (with rage-click guard)
  const isStartingSessionRef = useRef(false);
  const handleStartPowerDialer = useCallback(() => {
    // Hard guard: dialing is capped at 500. Over that, the button is disabled
    // and we refuse to start — the agent must trim the selection (or use the
    // bulk pipeline/source actions, which aren't dial-limited).
    if (selectedLeads.length > 500) return;
    if (selectedLeads.length === 0 || sessionActive || isStartingSessionRef.current) return;
    // Same gate as a manual call: without an active number every call would
    // connect to the "no caller id" message and drop, which looked like the
    // session "starting and immediately stopping". Say why instead.
    if (dialBlocked) {
      setShowNoPhoneDialog(true);
      if (dialBlocker) toast({ title: dialBlocker.title, description: dialBlocker.body, variant: "destructive", duration: 8000 });
      return;
    }
    isStartingSessionRef.current = true;
    startPowerDialer();
    setTimeout(() => { isStartingSessionRef.current = false; }, 1000);
  }, [selectedLeads.length, startPowerDialer, sessionActive, dialBlocked, dialBlocker, toast]);

  // Start multi-line dialer (with rage-click guard).
  // PLAN GATE: Multi-Line is Full-Fledged+ — paid BASIC gets Power Dialer only
  // (trials allowed; their dial-time cap is the limiter). This is the second
  // entry point (the widget's own button) — BulkDialerActions has the same gate.
  const isBasicPlanUser = ['basic', 'lite'].includes(String((user?.user as any)?.accountType || ''));
  const handleStartMultiLineDialer = useCallback(() => {
    if (isBasicPlanUser) {
      toast({
        title: "Multi-Line is a Full-Fledged feature",
        description: "Upgrade your plan to dial multiple lines in parallel. Power Dialer is included in your plan.",
      });
      return;
    }
    if (selectedLeads.length > 500) return; // dialing capped at 500
    if (selectedLeads.length === 0 || sessionActive || isStartingSessionRef.current) return;
    if (dialBlocked) {
      setShowNoPhoneDialog(true);
      if (dialBlocker) toast({ title: dialBlocker.title, description: dialBlocker.body, variant: "destructive", duration: 8000 });
      return;
    }
    isStartingSessionRef.current = true;
    startMultiLineDialer();
    setTimeout(() => { isStartingSessionRef.current = false; }, 1000);
  }, [selectedLeads.length, startMultiLineDialer, sessionActive, isBasicPlanUser, toast, dialBlocked, dialBlocker]);

  // Stop dialer session
  const handleStopDialer = useCallback(() => {
    stopDialerSession();
    setCurrentView('queue');
  }, [stopDialerSession]);

  // Handle dialer session complete - show completion or stopped screen with stats
  const handleDialerComplete = useCallback((stats?: {
    answered: number;
    called?: number;
    totalDialTime: number;
    talkSeconds?: number;
    conversationSeconds?: number;
    appointments?: number;
    underwriting?: number;
    sold?: number;
    wasStopped?: boolean;
  }) => {
    // Capture lead count before clearing
    const totalLeads = selectedLeads.length;

    stopDialerSession();
    // Store stats and show completion/stopped view
    setSessionStats({
      answered: stats?.answered || 0,
      called: stats?.called || stats?.answered || 0,
      totalDialTime: stats?.totalDialTime || 0,
      talkSeconds: stats?.talkSeconds || 0,
      conversationSeconds: stats?.conversationSeconds || 0,
      total: totalLeads,
      appointments: stats?.appointments || 0,
      underwriting: stats?.underwriting || 0,
      sold: stats?.sold || 0,
    });
    setSessionWasStopped(!!stats?.wasStopped);
    // Clear leads immediately when session ends - don't keep them in queue
    clearSelectedLeads();
    setShowSessionComplete(true);
    setCurrentView('queue'); // Show in queue view area
  }, [stopDialerSession, selectedLeads.length, clearSelectedLeads]);

  // Dismiss session complete/stopped and clear leads
  const handleDismissSessionComplete = useCallback(() => {
    setShowSessionComplete(false);
    setSessionWasStopped(false);
    setSessionStats({ answered: 0, called: 0, totalDialTime: 0, talkSeconds: 0, conversationSeconds: 0, total: 0, appointments: 0, underwriting: 0, sold: 0 });
    clearSelectedLeads();
    setCurrentView('keypad');
  }, [clearSelectedLeads]);

  // Resume an incomplete session from history
  const handleResumeSession = useCallback((session: DialerSession) => {
    if (!session.leadQueue || session.leadQueue.length === 0) return;

    const resumeIndex = session.currentLeadIndex || 0;

    // Determine dialer mode from session
    const mc = session.maxConcurrent ?? 1;
    const isPower = mc <= 1 || session.dialMode === 'power_dial';

    // Map all leads to the format the dialer expects
    const allLeads = session.leadQueue.map((lead) => ({
      id: lead.leadId || lead.id || '',
      name: lead.name || '',
      phone: lead.phone || lead.phoneNumber || '',
    })) as any[];

    // Both modes load the stored queue as-is.
    // Power: the queue is the FULL original list and the dialer resumes by position
    // (setCurrentLeadIndex below), so progress reads "6/50", not "1/43". Slicing it as well
    // (04-10) skipped the dialed leads twice: resuming at lead 77 of 155 opened lead 77 of
    // the remaining 79 (Mark Gurraj 10-17, "Called 76 out of 79" with nothing dialed).
    // Multi-line: the queue already holds only the remaining/re-queued leads, and
    // currentLeadIndex there counts processed calls, not a position.
    const leadsToLoad = allLeads;

    if (leadsToLoad.length === 0 || (isPower && resumeIndex >= leadsToLoad.length)) return;

    // Prevent the old session from being resumed again. The new canResume() check
    // is based on leadQueue.length - completedCalls > 0, so we clear the queue AND
    // bump currentLeadIndex. Both signals now agree this session is done, even though
    // we leave status='stopped' (the backend guard prevents 'completed' overwrites).
    dialerSessionApi.updateProgress(session.id, {
      currentLeadIndex: session.totalLeadsToCall,
      leadQueue: [],
    }).catch(() => {}); // fire-and-forget

    // Set leads and start dialer in one batch to avoid stale closure issues.
    // addLeadsToDialer syncs the ref immediately, then startPowerDialer reads from the ref.
    //
    // Restore source if this session was launched from DialIQ — that flips
    // dialerSource back to 'dialiq' so PowerDialerSession's mount-effect
    // engages predictive mode (joins the persistent conference, etc.).
    const restoredSource = session.source === 'dialiq' ? 'dialiq' : 'manual';
    // Power: the list goes first. Appended after leads already queued, the position pointed
    // into those and dialed the wrong people (~30 resumes 10-20→10-28).
    addLeadsToDialer(leadsToLoad, restoredSource, { first: isPower });
    if (isPower) {
      startPowerDialer(); // resets resumeOffset to 0
      // Set resume offset AFTER startPowerDialer so it overrides the reset.
      // React batches state updates — last call wins.
      // This tells PowerDialerSessionLogic to add the offset to completedCalls
      // so the backend shows cumulative progress (e.g., "10/50" not "4/50").
      setResumeOffset(resumeIndex);
      setTimeout(() => setCurrentLeadIndex(resumeIndex), 50);
    } else {
      startMultiLineDialer(); // resets resumeOffset to 0
      // For multi-line, resumeOffset reflects how many leads were already processed
      // (from completedCalls, not currentLeadIndex), so the session-history view shows
      // cumulative progress across the resumed run.
      setResumeOffset(session.completedCalls ?? 0);
    }
  }, [addLeadsToDialer, setCurrentLeadIndex, setResumeOffset, startPowerDialer, startMultiLineDialer]);

  // Keep ref in sync for auto-resume effect
  handleResumeSessionRef.current = handleResumeSession;

  // Get lead info for active call
  const leadName = isBridgedCall
    ? (activeBridgedCall?.leadName || "Unknown")
    : (callMetadata?.leadName || "Unknown Caller");
  const phoneNumber = isBridgedCall
    ? (activeBridgedCall?.leadPhone || "")
    : (callMetadata?.phoneNumber || "");
  const leadId = callMetadata?.leadId;

  // Clean phone number for lead lookup during active call
  const activeCallPhoneNumber = useMemo(() => {
    return phoneNumber.replace(/\D/g, '').slice(-10);
  }, [phoneNumber]);

  // Fetch full lead details during active call (for showing location, email, DOB etc.)
  // Try by leadId first, then fallback to phone number lookup
  const { data: activeCallLeadByIdResponse, isLoading: isLoadingLeadById } = useLead(leadId || '');
  // Always try phone lookup when we have an active call with a valid 10-digit number
  const { data: activeCallLeadByPhone, isLoading: isLoadingLeadByPhone } = useLeadByPhone(
    hasActiveCall && activeCallPhoneNumber.length === 10 ? activeCallPhoneNumber : ''
  );

  // Extract lead from response - useLead returns the full API response { data: Lead, message: string }
  // The actual Lead object is nested inside .data
  const activeCallLeadById = useMemo(() => {
    if (!activeCallLeadByIdResponse) return null;
    // Handle both wrapped { data: Lead } and direct Lead response formats
    if ('data' in activeCallLeadByIdResponse && activeCallLeadByIdResponse.data) {
      return activeCallLeadByIdResponse.data as Lead;
    }
    // If it's directly a Lead object (has 'name' property)
    if ('name' in activeCallLeadByIdResponse) {
      return activeCallLeadByIdResponse as unknown as Lead;
    }
    return null;
  }, [activeCallLeadByIdResponse]);

  // Loading state for lead details
  const isLoadingLeadDetails = (hasActiveCall && isLoadingLeadById) || (hasActiveCall && isLoadingLeadByPhone);

  // Use leadById if available, otherwise try phone lookup, finally fallback to preserved matchedLead from before the call
  const activeCallLead = activeCallLeadById || activeCallLeadByPhone || activeCallMatchedLead || (hasActiveCall ? matchedLead : null);

  // Handler to create lead during active call (for incoming unknown numbers)
  const handleCreateLeadFromActiveCall = useCallback(async () => {
    if (activeCallPhoneNumber.length !== 10) return;
    if (!activeCallNewLeadName.trim() || activeCallNewLeadName.trim().length < 2) {
      toast({
        title: "Name required",
        description: "Please enter a name (at least 2 characters).",
        variant: "destructive",
      });
      return;
    }

    setIsCreatingLead(true);
    try {
      const result = await createLeadMutation.mutateAsync({
        name: activeCallNewLeadName.trim(),
        phone: activeCallPhoneNumber,
      });

      // Invalidate the phone lookup query so the dialer shows the new lead
      await queryClient.invalidateQueries({
        queryKey: leadsQueryKeys.byPhone(activeCallPhoneNumber)
      });

      toast({
        title: "Lead created",
        description: `${activeCallNewLeadName.trim()} created successfully.`,
      });
      // Reset form state
      setShowActiveCallAddLeadForm(false);
      setActiveCallNewLeadName("");
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create lead. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCreatingLead(false);
    }
  }, [activeCallPhoneNumber, activeCallNewLeadName, createLeadMutation, toast, queryClient]);

  // Reset active call add lead form when call ends. Keep `showCallerId` true
  // so the "From:" line is visible by default on every new call — agents should
  // never have to click to see which outbound number the system used.
  useEffect(() => {
    if (!hasActiveCall) {
      setShowActiveCallAddLeadForm(false);
      setActiveCallNewLeadName("");
      setShowCallerId(true);
    }
  }, [hasActiveCall]);

  // Use constant dial pad digits (defined outside component for performance)

  // Handle add party dial pad press (must be before early returns for hooks rules)
  // ---- LIVE TRANSFER (2026-07-23): agent → upline agency owner ----
  const [showTransferPanel, setShowTransferPanel] = useState(false);
  const [transferTargets, setTransferTargets] = useState<Array<{ userId: string; name: string; agencyName: string; depth: number; availability: 'available' | 'busy' | 'offline'; reachableVia?: 'crm' | 'cell' | 'both' }>>([]);
  const [isLoadingTransferTargets, setIsLoadingTransferTargets] = useState(false);
  const [pendingTransfer, setPendingTransfer] = useState<{ targetName: string; conferenceSid: string; participantCallSid: string; phoneParticipantCallSid?: string; callSid: string; isBack?: boolean } | null>(null);
  const pendingTransferRef = useRef(pendingTransfer);
  useEffect(() => { pendingTransferRef.current = pendingTransfer; }, [pendingTransfer]);
  // Owner viewing a call that was transferred TO them → the button becomes
  // "Transfer Back" (the targets list leads with the original agent).
  const isTransferredInCall = (callMetadata as any)?.source === 'live_transfer';
  // Owner 2026-11-02: no call transfer on Direct 130's address.
  const onDirect130 = useDirect130Host();

  const loadTransferTargets = useCallback(async () => {
    setIsLoadingTransferTargets(true);
    try {
      const sidQ = ((callMetadata as any)?.parentCallSid || activeCallSid) ? `?callSid=${encodeURIComponent((callMetadata as any)?.parentCallSid || activeCallSid || '')}` : '';
      const res = await api.get<any>({ url: getApiUrl('/communication/transfer/targets') + sidQ, auth: true });
      const data = (res as any)?.data?.data ?? (res as any)?.data ?? [];
      setTransferTargets(Array.isArray(data) ? data : []);
    } catch {
      setTransferTargets([]);
    } finally {
      setIsLoadingTransferTargets(false);
    }
  }, [callMetadata, activeCallSid]);

  // PREFETCH on connect (2026-07-23): fetch targets once per call so the
  // Transfer button only renders when the agent actually HAS somewhere to
  // send the call — an agent with no uplines never sees a dead-end button.
  const transferTargetsFetchedForSid = useRef<string | null>(null);
  useEffect(() => {
    const sid = (callMetadata as any)?.parentCallSid || activeCallSid;
    if (onDirect130 || !sid || callStatus !== 'in-progress') return;
    if (transferTargetsFetchedForSid.current === sid) return;
    transferTargetsFetchedForSid.current = sid;
    loadTransferTargets();
  }, [onDirect130, callMetadata, activeCallSid, callStatus, loadTransferTargets]);

  const handleInitiateTransfer = useCallback(async (target: { userId: string; name: string; depth?: number }) => {
    // Transferred-in calls: the meaningful sid is the ORIGINAL call's (stored
    // as parentCallSid at accept) — the owner's own leg sid keys nothing.
    const callSid = (callMetadata as any)?.parentCallSid || activeCallSid;
    if (!callSid) { toast({ title: "No active call to transfer", variant: "destructive" }); return; }
    try {
      const res = await api.post<any>({
        url: getApiUrl('/communication/transfer/initiate'),
        body: {
          callSid,
          targetUserId: target.userId,
          // Transferred-in calls: the holder's FE knows the live conference
          // (stored at accept) — initiate verifies membership before using it
          // (the parent leg is gone, so it can't be found by sid scan).
          conferenceSid: (callMetadata as any)?.transferConferenceSid || undefined,
          leadId: matchedLead?.id,
          leadName: matchedLead?.name || callMetadata?.leadName,
          // customerNumber intentionally NOT sent — the backend derives the
          // caller-ID from the call record (security review 2026-07-23).
        },
        auth: true,
      });
      const d = (res as any)?.data?.data ?? (res as any)?.data;
      if (d?.participantCallSid) {
        const isBack = (target.depth ?? 0) < 0;
        setPendingTransfer({ targetName: target.name, conferenceSid: d.conferenceSid, participantCallSid: d.participantCallSid, phoneParticipantCallSid: d.phoneParticipantCallSid, callSid, isBack });
        setShowTransferPanel(false);
        if (isBack) {
          toast({ title: `Transferring back to ${target.name}`, description: "They're being called now — you'll drop off when they accept." });
        }
      } else {
        toast({ title: (res as any)?.data?.message || "Couldn't start transfer", variant: "destructive" });
      }
    } catch (e: any) {
      toast({ title: e?.message || "Couldn't start transfer", variant: "destructive" });
    }
  }, [activeCallSid, callMetadata, matchedLead, toast]);

  const handleCancelTransfer = useCallback(async () => {
    const pt = pendingTransferRef.current;
    setPendingTransfer(null);
    if (!pt) return;
    api.post({
      url: getApiUrl('/communication/transfer/resolve'),
      body: { action: 'cancel', callSid: pt.callSid, conferenceSid: pt.conferenceSid, participantCallSid: pt.participantCallSid, phoneParticipantCallSid: pt.phoneParticipantCallSid, isBack: pt.isBack },
      auth: true,
    }).catch(() => {});
  }, []);

  // Owner accepted → drop OUR device leg only (server cancel skipped — the
  // customer + owner keep talking). Declined → keep the call, tell the agent.
  useEffect(() => {
    const onStatus = (e: any) => {
      const d = e?.detail || {};
      const pt = pendingTransferRef.current;
      if (!pt || d.callSid !== pt.callSid) return;
      if (d.status === 'accepted') {
        setPendingTransfer(null);
        toast({ title: `Transferred to ${pt.targetName}`, description: "You've been disconnected — the call continues." });
        // Device drop happens centrally in TwilioDeviceContext (transfer:status listener).
      } else if (d.status === 'declined') {
        setPendingTransfer(null);
        toast({ title: `${pt.targetName} declined the transfer`, description: "You're still on the call.", variant: "destructive" });
      }
    };
    window.addEventListener('transfer:status', onStatus);
    // FAST-PATH (2026-07-23): participant-join webhook = owner answered — fire
    // the same handling instantly instead of waiting for the resolve broadcast.
    const onJoined = (e: any) => {
      const pt = pendingTransferRef.current;
      const sid = e?.detail?.participantSid;
      if (!pt || (sid !== pt.participantCallSid && sid !== pt.phoneParticipantCallSid)) return;
      // CELL WON (call-forwarding, 2026-07-23): the owner answered on their
      // phone — record the accept + tear down the still-ringing browser leg
      // so the owner can't double-join from the CRM card.
      if (sid === pt.phoneParticipantCallSid && sid !== pt.participantCallSid) {
        api.post({
          url: getApiUrl('/communication/transfer/resolve'),
          body: {
            action: 'accepted', callSid: pt.callSid, targetName: pt.targetName,
            conferenceSid: pt.conferenceSid,
            cancelLegSid: pt.participantCallSid !== pt.phoneParticipantCallSid ? pt.participantCallSid : undefined,
          },
          auth: true,
        }).catch(() => {});
      }
      window.dispatchEvent(new CustomEvent('transfer:status', { detail: { status: 'accepted', callSid: pt.callSid, targetName: pt.targetName } }));
    };
    window.addEventListener('transfer:participant-joined', onJoined);
    return () => {
      window.removeEventListener('transfer:status', onStatus);
      window.removeEventListener('transfer:participant-joined', onJoined);
    };
  }, [disconnect, toast]);

  // Transfer ring timeout — if the owner never answers, clear the pending
  // state + tear down the leg (30s leg timeout + 5s grace).
  useEffect(() => {
    if (!pendingTransfer) return;
    const t = setTimeout(() => {
      if (pendingTransferRef.current) {
        toast({ title: `${pendingTransferRef.current.targetName} didn't answer`, description: "You're still on the call.", variant: "destructive" });
        handleCancelTransfer();
      }
    }, 35000);
    return () => clearTimeout(t);
  }, [pendingTransfer, handleCancelTransfer, toast]);

  const handleAddPartyDialPadPress = useCallback((digit: string) => {
    if (cleanedAddPartyNumber.length < 10) {
      setAddPartyNumber(prev => prev + digit);
    }
  }, [cleanedAddPartyNumber.length]);

  // Handle add party input change
  const handleAddPartyInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const digits = value.replace(/\D/g, "").slice(0, 10);
    setAddPartyNumber(digits);
  }, []);

  // Handle adding participant to call
  const handleAddPartyToCall = useCallback(async () => {
    if (cleanedAddPartyNumber.length !== 10 || !addParticipantToCall) return;

    const formattedNumber = "+1" + cleanedAddPartyNumber;
    const name = addPartyMatchedLead?.name || undefined;

    await addParticipantToCall(formattedNumber, name);
    setAddPartyNumber("");
    setShowAddPartyPanel(false); // Close the panel after adding
  }, [cleanedAddPartyNumber, addParticipantToCall, addPartyMatchedLead]);

  // Global keyboard listener for add party panel - type phone number
  useEffect(() => {
    // Only active when add party panel is open and widget is expanded
    if (!showAddPartyPanel || isMinimized) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const activeElement = document.activeElement;
      const isInputFocused = activeElement?.tagName === 'INPUT' ||
                            activeElement?.tagName === 'TEXTAREA' ||
                            activeElement?.getAttribute('contenteditable') === 'true';

      if (isInputFocused) return;

      // Handle number keys (0-9)
      if (/^[0-9]$/.test(e.key)) {
        e.preventDefault();
        handleAddPartyDialPadPress(e.key);
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        setAddPartyNumber(prev => prev.slice(0, -1));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showAddPartyPanel, isMinimized, handleAddPartyDialPadPress]);

  // LIVE TRANSFER offer card (owner side, 2026-07-23) — rendered BEFORE the
  // isWidgetOpen gate: the receiving owner is usually idle with the dialer
  // closed, and the offer must surface anyway. Fixed overlay, own stacking.
  const transferOfferCard = incomingTransferOffer ? (
    <div
      className="fixed z-[9999] w-[340px] rounded-2xl border bg-background shadow-2xl overflow-hidden animate-in slide-in-from-top-4"
      // COLLISION (2026-07-23): a REAL incoming call renders its widget at the
      // same top-right spot — when both ring at once, stack the transfer card
      // BELOW it so the owner sees and can choose between both.
      style={{ top: ringingIncomingCall ? 'calc(5rem + 270px)' : '5rem', right: '1.25rem' }}
    >
      {/* accent bar — mirrors the incoming-call widget */}
      <div className="h-1 w-full bg-emerald-500" aria-hidden />
      <div className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
          </span>
          <span className="text-[11px] font-semibold uppercase tracking-wide text-emerald-600">Incoming Transfer Call</span>
        </div>
        <div className="flex items-center gap-3 mb-1">
          <div className="h-11 w-11 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
            <span className="text-lg font-semibold text-emerald-700">
              {(incomingTransferOffer.leadName || incomingTransferOffer.customerNumber || '?').charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="min-w-0">
            <div className="text-base font-semibold truncate">
              {incomingTransferOffer.leadName || (incomingTransferOffer.customerNumber ? formatPhoneNumber(incomingTransferOffer.customerNumber) : 'Live caller')}
            </div>
            {incomingTransferOffer.leadName && incomingTransferOffer.customerNumber && (
              <div className="text-xs text-muted-foreground">{formatPhoneNumber(incomingTransferOffer.customerNumber)}</div>
            )}
          </div>
        </div>
        <div className="text-xs text-muted-foreground mb-3">
          <span className="font-medium text-foreground">{incomingTransferOffer.agentName}</span> is transferring this live call to you
        </div>
        <div className="flex gap-2">
          <Button
            className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white"
            onClick={async () => {
              // Capture BEFORE accept — accepting clears incomingTransferOffer.
              const leadIdToOpen = incomingTransferOffer?.leadId;
              const ok = await acceptTransferOffer();
              if (ok) {
                // Same post-accept experience as answering an incoming call:
                // widget opens on the standard active-call view + lead page.
                openWidget();
                maximizeWidget();
                toast({ title: "Transfer connected", description: "You're now live with the customer." });
                if (leadIdToOpen) router.push(`/leads/${leadIdToOpen}`);
              } else {
                toast({ title: "Couldn't join the call", variant: "destructive" });
              }
            }}
          >
            Accept call
          </Button>
          <Button variant="outline" className="flex-1 text-red-600 border-red-200 hover:bg-red-50" onClick={() => declineTransferOffer()}>
            Decline
          </Button>
        </div>
      </div>
    </div>
  ) : null;

  // Don't render if widget is not open
  if (!isWidgetOpen) return transferOfferCard;

  // Note: We no longer early-return for minimized state
  // Instead, we render both the minimized button AND keep session components mounted (hidden)
  // This preserves the isPaused state when toggling minimize/maximize

  /** Masked field: digits the customer keys in are captured, never shown. Reads at 14px. */
  const KeyGrabberGlyph = ({ className }: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden>
      <rect x="2" y="6.5" width="20" height="11" rx="3.5" stroke="currentColor" strokeWidth="2" />
      {[7.5, 12, 16.5].map((x) => <circle key={x} cx={x} cy="12" r="1.8" fill="currentColor" />)}
    </svg>
  );

  // Render active call UI with tabs (like InsuraBot)
  const renderActiveCall = () => (
    <Tabs value={callTab} onValueChange={(v) => setCallTab(v as 'call' | 'insurabot')} className="flex-1 flex flex-col min-h-0">
      <TabsList data-tour="dialer-widget-tabs" className="inline-flex w-full mx-4 mt-2 mb-3" style={{ width: 'calc(100% - 32px)' }}>
        <TabsTrigger value="call" className="flex-1 text-xs">
          <Phone className="h-3.5 w-3.5 mr-1.5" />
          Call
          {conferenceParticipants && conferenceParticipants.length > 0 && (
            <span className="ml-1 bg-primary text-primary-foreground text-[10px] rounded-full h-4 w-4 flex items-center justify-center">
              {conferenceParticipants.length}
            </span>
          )}
        </TabsTrigger>
        <TabsTrigger data-tour="dialer-insurabot-tab" value="insurabot" className="flex-1 text-xs">
          <KeyGrabberGlyph className="h-3.5 w-3.5 mr-1.5" />
          InsuraBot
        </TabsTrigger>
      </TabsList>

      <TabsContent value="call" className="flex-1 mt-0 px-4 pb-4 overflow-y-auto">
        {/* Supervisor Coach Banner — visible only when whisper or barge is
            active. Listen mode is silent by design (no banner). The banner
            stays up for the duration of the supervisor session and clears
            when they stop or the call ends. */}
        {coachActive && (
          <div className={`mb-3 rounded-lg border-2 px-3 py-2 flex items-center gap-2.5 animate-pulse ${
            coachActive.mode === 'barge'
              ? 'border-amber-400 bg-amber-50'
              : 'border-blue-400 bg-blue-50'
          }`}>
            <div className={`h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 ${
              coachActive.mode === 'barge' ? 'bg-amber-500' : 'bg-blue-500'
            }`}>
              {coachActive.mode === 'barge' ? (
                <Megaphone className="h-3.5 w-3.5 text-white" />
              ) : (
                <Headphones className="h-3.5 w-3.5 text-white" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-xs font-bold ${
                coachActive.mode === 'barge' ? 'text-amber-900' : 'text-blue-900'
              }`}>
                {coachActive.mode === 'barge' ? 'Supervisor on the call' : 'Supervisor whispering'}
              </p>
              <p className={`text-[10px] ${
                coachActive.mode === 'barge' ? 'text-amber-700' : 'text-blue-700'
              }`}>
                {coachActive.mode === 'barge'
                  ? `${coachActive.managerName} — lead can hear them`
                  : `${coachActive.managerName} is coaching you (lead can't hear)`}
              </p>
            </div>
          </div>
        )}

        {/* Combined Call Info & Lead Details Card */}
        <div className="mb-4 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          {/* Header with name, status, and call duration */}
          <div className={`px-3 py-2.5 border-b border-gray-100 ${
            isMergedCall
              ? 'bg-gradient-to-r from-amber-50 to-orange-50'
              : heldCall
                ? 'bg-gradient-to-r from-violet-50 to-indigo-50'
                : 'bg-gradient-to-r from-blue-50 to-indigo-50'
          }`}>
            <div className="flex items-center gap-2.5">
              <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 text-white ${
                isMergedCall ? 'bg-amber-500' : heldCall ? 'bg-violet-500' : 'bg-blue-500'
              }`}>
                {isMergedCall ? <Users className="h-5 w-5" /> : <User className="h-5 w-5" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  {isMergedCall ? (
                    <h3 className="font-semibold text-gray-900 truncate text-sm">
                      {leadName}{heldCall ? ` & ${heldCall.callerName}` : ''}
                    </h3>
                  ) : (
                    <h3 className="font-semibold text-gray-900 truncate text-sm">{leadName}</h3>
                  )}
                  {isMergedCall ? (
                    <Badge className="bg-amber-500 text-white text-[9px] px-1.5 py-0 h-4 select-none cursor-default">
                      3-WAY
                    </Badge>
                  ) : (
                    <Badge className={`${getStatusColor()} text-[9px] px-1.5 py-0 h-4 select-none cursor-default`}>
                      {getStatusText()}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <p className="text-xs text-gray-500">{formatPhone(phoneNumber)}</p>
                  {(callMetadata as any)?.source === 'live_transfer' && (
                    <Badge className="bg-emerald-600 text-white text-[9px] px-1.5 py-0 h-4 select-none cursor-default whitespace-nowrap flex-shrink-0">
                      <PhoneForwarded className="h-2.5 w-2.5 mr-0.5" />
                      TRANSFERRED
                    </Badge>
                  )}
                  {callMetadata?.outboundCallerId && (
                    <button
                      onClick={() => setShowCallerId(!showCallerId)}
                      className={`p-0.5 rounded ${showCallerId ? 'text-emerald-600 hover:text-emerald-700' : 'text-gray-400 hover:text-emerald-600'} transition-colors`}
                      title={showCallerId ? 'Hide outbound caller ID' : 'Show outbound caller ID'}
                    >
                      {showCallerId ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                    </button>
                  )}
                </div>
                {/* Held-call indicator: shown when agent pressed "Hold & Answer" —
                    the prior caller is frozen with hold music, waiting to resume. */}
                {heldCall && !isMergedCall && (
                  <div className="mt-1 flex items-center gap-1.5">
                    <PauseCircle className="h-3 w-3 text-violet-600" />
                    <p className="text-[10px] text-violet-700 font-medium truncate">
                      On hold: {heldCall.callerName}
                    </p>
                  </div>
                )}
                {showCallerId && callMetadata?.outboundCallerId && (
                  <p className="text-xs text-emerald-600 leading-normal font-medium">From: {formatPhone(callMetadata.outboundCallerId)}</p>
                )}
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <span className="text-sm font-mono text-gray-700">
                  {formatDuration(callDuration)}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className={`h-7 w-7 p-0 ${showQuickNotes ? 'text-amber-600 bg-amber-50' : 'text-gray-400 hover:text-amber-600 hover:bg-amber-50'}`}
                  onClick={() => setShowQuickNotes(!showQuickNotes)}
                  title="Quick Notes"
                >
                  <StickyNote className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </div>

          {/* Quick Notes Panel - shown during active call */}
          {showQuickNotes && activeCallLead && (
            <div className="px-3 py-2 border-b border-gray-100">
              <QuickNotesPanel
                leadId={activeCallLead.id}
                leadName={activeCallLead.name}
                onClose={() => setShowQuickNotes(false)}
              />
            </div>
          )}

          {/* Lead Details Section - Only show fields with values */}
          {activeCallLead && (
            <div className="px-3 py-2">
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                {/* Email */}
                {activeCallLead.email && (
                  <div className="text-center">
                    <span className="text-gray-400">Email</span>
                    <p className="text-gray-700 truncate">{activeCallLead.email}</p>
                  </div>
                )}

                {/* Age - next to email */}
                {activeCallLead.age && (
                  <div className="text-center">
                    <span className="text-gray-400">Age</span>
                    <p className="text-gray-700">{activeCallLead.age} years</p>
                  </div>
                )}

                {/* DOB */}
                {(activeCallLead.dob || activeCallLead.birthday) && (
                  <div className="text-center">
                    <span className="text-gray-400">DOB</span>
                    <p className="text-gray-700">
                      {(() => {
                        const dobStr = activeCallLead.dob || activeCallLead.birthday;
                        try {
                          const [year, month, day] = dobStr!.split('T')[0].split('-').map(Number);
                          return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
                        } catch {
                          return dobStr;
                        }
                      })()}
                    </p>
                  </div>
                )}

                {/* Gender */}
                {activeCallLead.gender && (
                  <div className="text-center">
                    <span className="text-gray-400">Gender</span>
                    <p className="text-gray-700 capitalize">{activeCallLead.gender}</p>
                  </div>
                )}

                {/* Marital Status */}
                {(activeCallLead.marital || activeCallLead.maritalStatus) && (
                  <div className="text-center">
                    <span className="text-gray-400">Marital</span>
                    <p className="text-gray-700 capitalize">{activeCallLead.marital || activeCallLead.maritalStatus}</p>
                  </div>
                )}

                {/* Height */}
                {activeCallLead.height && (
                  <div className="text-center">
                    <span className="text-gray-400">Height</span>
                    <p className="text-gray-700">{activeCallLead.height}</p>
                  </div>
                )}

                {/* Weight */}
                {activeCallLead.weight && (
                  <div className="text-center">
                    <span className="text-gray-400">Weight</span>
                    <p className="text-gray-700">{activeCallLead.weight} lbs</p>
                  </div>
                )}

                {/* Address - under height */}
                {activeCallLead.address && (
                  <div className="text-center">
                    <span className="text-gray-400">Address</span>
                    <p className="text-gray-700 break-words">{activeCallLead.address}</p>
                  </div>
                )}

                {/* State/Zip - under weight */}
                {(activeCallLead.state || activeCallLead.zipCode) && (
                  <div className="text-center">
                    <span className="text-gray-400">State/Zip</span>
                    <p className="text-gray-700">{[activeCallLead.state, activeCallLead.zipCode].filter(Boolean).join(' ')}</p>
                  </div>
                )}

                {/* City - full width centered */}
                {activeCallLead.city && (
                  <div className="col-span-2 text-center">
                    <span className="text-gray-400">City</span>
                    <p className="text-gray-700">{activeCallLead.city}</p>
                  </div>
                )}

                {/* Employment - full width centered */}
                {(activeCallLead.employer || activeCallLead.employmentStatus) && (
                  <div className="col-span-2 text-center">
                    <span className="text-gray-400">Employment</span>
                    <p className="text-gray-700 break-words">{activeCallLead.employer || activeCallLead.employmentStatus}</p>
                  </div>
                )}
              </div>

              {/* List name badge */}
              {activeCallLead.listName && (
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <span className="text-[10px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                    {activeCallLead.listName}
                  </span>
                </div>
              )}

              {/* Show message if lead exists but has no displayable fields */}
              {!activeCallLead.email &&
               !activeCallLead.dob &&
               !activeCallLead.birthday &&
               !activeCallLead.age &&
               !activeCallLead.gender &&
               !activeCallLead.marital &&
               !activeCallLead.maritalStatus &&
               !activeCallLead.height &&
               !activeCallLead.weight &&
               !activeCallLead.address &&
               !activeCallLead.city &&
               !activeCallLead.state &&
               !activeCallLead.zipCode &&
               !activeCallLead.employer &&
               !activeCallLead.employmentStatus &&
               !activeCallLead.listName && (
                <p className="text-xs text-gray-400 italic">No additional details available</p>
              )}
            </div>
          )}

          {/* No lead data available - still loading or no details found */}
          {!activeCallLead && hasActiveCall && (
            <div className="px-3 py-3 border-t border-gray-100">
              {isLoadingLeadDetails ? (
                <div className="flex items-center justify-center gap-2">
                  <Loader2 className="h-3 w-3 animate-spin text-gray-400" />
                  <p className="text-xs text-gray-400">Loading lead details...</p>
                </div>
              ) : !showActiveCallAddLeadForm ? (
                <div className="flex flex-col items-center gap-2">
                  <p className="text-xs text-gray-400">No lead details found</p>
                  {activeCallPhoneNumber.length === 10 && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1.5 h-7 text-xs"
                      onClick={() => setShowActiveCallAddLeadForm(true)}
                    >
                      <UserPlus className="h-3.5 w-3.5" />
                      Add as Lead
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="space-y-1">
                    <label className="text-xs text-gray-500 font-medium">Lead Name</label>
                    <Input
                      placeholder="Enter name..."
                      value={activeCallNewLeadName}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => setActiveCallNewLeadName(e.target.value)}
                      className="h-8 text-sm"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && activeCallNewLeadName.trim().length >= 2) {
                          handleCreateLeadFromActiveCall();
                        }
                      }}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="flex-1 h-7 text-xs"
                      onClick={() => {
                        setShowActiveCallAddLeadForm(false);
                        setActiveCallNewLeadName("");
                      }}
                      disabled={isCreatingLead}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      className="flex-1 gap-1 h-7 text-xs"
                      onClick={handleCreateLeadFromActiveCall}
                      disabled={isCreatingLead || activeCallNewLeadName.trim().length < 2}
                    >
                      {isCreatingLead ? (
                        <>
                          <Loader2 className="h-3 w-3 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        <>
                          <UserPlus className="h-3 w-3" />
                          Create
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Conference Participants - Show inline when there are participants */}
        {conferenceParticipants && conferenceParticipants.length > 0 && (
          <div className="mb-4 p-2 border rounded-lg bg-blue-50">
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-4 w-4 text-blue-600" />
              <span className="text-xs font-medium text-blue-800">
                3-Way Call ({conferenceParticipants.length + 2} participants)
              </span>
            </div>
            <div className="space-y-1.5">
              {conferenceParticipants.map((participant) => (
                <div key={participant.participantSid} className="flex items-center justify-between p-1.5 bg-white rounded border border-blue-200">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="h-6 w-6 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                      <User className="h-3 w-3 text-white" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-medium truncate">{participant.name || formatPhone(participant.phoneNumber || '')}</p>
                      <p className="text-[10px] text-gray-500">
                        {participant.status === 'connected' ? (
                          <span className="text-green-600">Connected</span>
                        ) : participant.status === 'ringing' ? (
                          <span className="text-yellow-600">Ringing...</span>
                        ) : participant.status === 'hold' ? (
                          <span className="text-orange-600">On Hold</span>
                        ) : participant.status === 'disconnected' ? (
                          <span className="text-gray-500">Disconnected</span>
                        ) : participant.status === 'failed' ? (
                          <span className="text-red-600">Failed</span>
                        ) : (
                          <span className="text-gray-500">Connecting...</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {/* Individual hold button for this participant */}
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`h-6 w-6 p-0 ${participant.isOnHold ? 'text-amber-500 hover:text-amber-600' : 'text-gray-500 hover:text-gray-600'}`}
                      onClick={() => holdParticipant?.(participant.participantSid, !participant.isOnHold)}
                      title={participant.isOnHold ? 'Resume participant' : 'Hold participant'}
                    >
                      <Pause className="h-3 w-3" />
                    </Button>
                    {/* Remove participant button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-red-500 hover:text-red-600"
                      onClick={() => removeParticipant?.(participant.participantSid)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Call Controls */}
        <div className="flex items-center justify-center gap-2 mb-4 flex-wrap">
          {isBridgedCall ? (
            // Phone forwarding controls - limited to what Twilio supports
            <>
              {/* Add Party - Supported via conference API */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="lg"
                      className={`rounded-full h-12 w-12 transition-all ${showAddPartyPanel ? "bg-blue-500 hover:bg-blue-600 text-white border-blue-500" : "hover:bg-gray-100 hover:text-gray-900"}`}
                      onClick={() => {
                        setShowAddPartyPanel(!showAddPartyPanel);
                      }}
                    >
                      <UserPlus className="h-5 w-5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="font-medium">Add Party to Call</p>
                    <p className="text-xs text-gray-400">Add another participant to this call</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              {/* Info badge: Use phone buttons for mute/hold */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-full text-sm text-blue-700 cursor-help">
                      <Info className="h-4 w-4" />
                      <span className="font-medium">Use phone for mute/hold</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="font-medium">Call Forwarding Active</p>
                    <p className="text-xs text-gray-400 mb-1">Use your phone's built-in mute and hold buttons.</p>
                    <p className="text-xs text-gray-400">Hangup and Add Party work via the app.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </>
          ) : (
            // Browser call controls - full feature set
            <>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="lg"
                      className={`rounded-full h-12 w-12 transition-all ${isMuted ? "bg-red-500 hover:bg-red-600 text-white border-red-500" : "hover:bg-gray-100 hover:text-gray-900"}`}
                      onClick={() => isMuted ? unmute() : mute()}
                    >
                      {isMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{isMuted ? 'Unmute' : 'Mute'}</TooltipContent>
                </Tooltip>
                {/* Speakerphone — native only. WKWebView has no setSinkId, so
                    without the AVAudioSession bridge iOS pins call audio to the
                    EARPIECE with no way out: agents could not work hands-free
                    at all. Hidden on web, where the OS already handles it. */}
                {supportsSpeakerphone() && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="lg"
                        className={`rounded-full h-12 w-12 transition-all ${speakerOn ? "bg-primary hover:bg-primary/90 text-white border-primary" : "hover:bg-gray-100 hover:text-gray-900"}`}
                        onClick={async () => {
                          const next = !speakerOn;
                          setSpeakerOn(next);
                          const applied = await setSpeakerphoneNative(next);
                          // Reflect what the OS actually did, not what we asked.
                          setSpeakerOn(applied);
                        }}
                      >
                        {speakerOn ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{speakerOn ? 'Speaker on' : 'Speaker off'}</TooltipContent>
                  </Tooltip>
                )}
              </TooltipProvider>

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="lg"
                      className={`rounded-full h-12 w-12 transition-all ${isOnHold ? "bg-amber-500 hover:bg-amber-600 text-white border-amber-500" : "hover:bg-gray-100 hover:text-gray-900"}`}
                      onClick={() => isOnHold ? unhold() : hold()}
                    >
                      {isOnHold ? <Play className="h-5 w-5" /> : <Pause className="h-5 w-5" />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{isOnHold ? 'Resume' : 'Hold'}</TooltipContent>
                </Tooltip>
              </TooltipProvider>

              {/* Dial Pad Button */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="lg"
                      className={`rounded-full h-12 w-12 transition-all ${showActiveCallDialPad ? "bg-purple-500 hover:bg-purple-600 text-white border-purple-500" : "hover:bg-gray-100 hover:text-gray-900"}`}
                      onClick={() => {
                        setShowActiveCallDialPad(!showActiveCallDialPad);
                        if (!showActiveCallDialPad) setShowAddPartyPanel(false); // Close add party if opening dial pad
                      }}
                    >
                      <Hash className="h-5 w-5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Dial Pad</TooltipContent>
                </Tooltip>
              </TooltipProvider>

              {/* Add Party Button */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="lg"
                      className={`rounded-full h-12 w-12 transition-all ${showAddPartyPanel ? "bg-blue-500 hover:bg-blue-600 text-white border-blue-500" : "hover:bg-gray-100 hover:text-gray-900"}`}
                      onClick={() => {
                        setShowAddPartyPanel(!showAddPartyPanel);
                        if (!showAddPartyPanel) setShowActiveCallDialPad(false); // Close dial pad if opening add party
                      }}
                    >
                      <UserPlus className="h-5 w-5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="font-medium">Add Party to Call</p>
                    <p className="text-xs text-gray-400">Add another participant to this call</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              {/* Live Transfer to upline agency owner (2026-07-23).
                  Rendered when the prefetch found somewhere to send the call —
                  agents with no uplines never see it. TRANSFERRED-IN calls
                  ALWAYS show it ("Transfer Back"): the send-back target exists
                  even for a top-of-hierarchy owner with zero uplines, so never
                  let a slow/empty prefetch hide it. */}
              {!onDirect130 && (transferTargets.length > 0 || isTransferredInCall) && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="lg"
                        className={`rounded-full h-12 w-12 transition-all ${showTransferPanel ? "bg-emerald-500 hover:bg-emerald-600 text-white border-emerald-500" : "hover:bg-gray-100 hover:text-gray-900"}`}
                        onClick={() => {
                          const next = !showTransferPanel;
                          setShowTransferPanel(next);
                          if (next) { loadTransferTargets(); setShowAddPartyPanel(false); setShowActiveCallDialPad(false); }
                        }}
                      >
                        <PhoneForwarded className="h-5 w-5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="font-medium">{isTransferredInCall ? 'Transfer Back' : 'Transfer Call'}</p>
                      <p className="text-xs text-gray-400">
                        {isTransferredInCall
                          ? 'Send this call back to the agent, or up your upline'
                          : 'Warm-transfer this call to an agency owner'}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </>
          )}
        </div>

        {/* Dial Pad UI - shown when dial pad button is active */}
        {showActiveCallDialPad && !isBridgedCall && (
          <div className="mb-4 p-2 border rounded-lg bg-gray-50">
            <div className="grid grid-cols-3 gap-1.5">
              {DIAL_PAD_DIGITS.map(({ digit, letters }) => (
                <button
                  key={digit}
                  type="button"
                  className="h-10 rounded-lg bg-white hover:bg-gray-100 active:bg-gray-200 border border-gray-200 transition-all flex flex-col items-center justify-center"
                  onClick={() => sendDigits?.(digit)}
                >
                  <span className="text-lg font-semibold text-gray-800">{digit}</span>
                  {letters && (
                    <span className="text-[8px] text-gray-400 tracking-wider">{letters}</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Add Party Panel - shown when add party button is active */}
        {showAddPartyPanel && (
          <div className="mb-4 p-3 border rounded-lg bg-blue-50 border-blue-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <UserPlus className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">Add Party to Call</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 text-blue-600 hover:text-blue-800"
                onClick={() => setShowAddPartyPanel(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Phone Input */}
            <div className="flex items-center gap-2 mb-2">
              <Input
                type="tel"
                value={formatDialDisplay(addPartyNumber)}
                onChange={handleAddPartyInputChange}
                placeholder="(XXX) XXX-XXXX"
                className="text-center text-sm font-mono h-9 border-blue-200 focus:border-blue-500 flex-1 bg-white"
                maxLength={14}
              />
              {addPartyNumber && (
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full h-9 w-9 flex-shrink-0 border-blue-200"
                  onClick={() => setAddPartyNumber(prev => prev.slice(0, -1))}
                >
                  <Delete className="h-4 w-4" />
                </Button>
              )}
            </div>

            {/* Lead Details - Show when full number entered and lead found */}
            {cleanedAddPartyNumber.length === 10 && addPartyMatchedLead ? (
              <div className="mb-2 p-2 rounded-lg border bg-white border-blue-200">
                <div className="flex items-center gap-2">
                  <div className="h-7 w-7 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 text-white text-xs font-semibold">
                    {addPartyMatchedLead.name?.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase() || 'L'}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{addPartyMatchedLead.name}</p>
                    <p className="text-xs text-gray-500">{formatDialDisplay(addPartyNumber)}</p>
                  </div>
                </div>
              </div>
            ) : cleanedAddPartyNumber.length === 10 ? (
              <div className="mb-2 p-2 rounded-lg border bg-white border-gray-200">
                <div className="flex items-center gap-2">
                  <div className="h-7 w-7 rounded-full bg-gray-400 flex items-center justify-center flex-shrink-0">
                    <User className="h-3.5 w-3.5 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">Unknown Number</p>
                    <p className="text-xs text-gray-500">{formatDialDisplay(addPartyNumber)}</p>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Dial Pad for Add Party */}
            <div className="grid grid-cols-3 gap-1 mb-2">
              {DIAL_PAD_DIGITS.map(({ digit, letters }) => (
                <button
                  key={digit}
                  type="button"
                  className="h-9 rounded-lg bg-white hover:bg-gray-50 active:bg-gray-100 border border-gray-200 transition-all flex flex-col items-center justify-center"
                  onClick={() => handleAddPartyDialPadPress(digit)}
                >
                  <span className="text-base font-semibold text-gray-800">{digit}</span>
                  {letters && (
                    <span className="text-[7px] text-gray-400 tracking-wider">{letters}</span>
                  )}
                </button>
              ))}
            </div>

            {/* Add to Call Button */}
            <Button
              className={`w-full h-10 text-sm font-medium rounded-lg ${
                cleanedAddPartyNumber.length === 10
                  ? "bg-green-500 hover:bg-green-600 text-white"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
              onClick={handleAddPartyToCall}
              disabled={cleanedAddPartyNumber.length !== 10 || isAddingParticipant || !addParticipantToCall}
            >
              {isAddingParticipant ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add to Call
                </>
              )}
            </Button>

            {/* Current Participants */}
            {conferenceParticipants && conferenceParticipants.length > 0 && (
              <div className="mt-2 pt-2 border-t border-blue-200">
                <div className="flex items-center gap-2 mb-1.5">
                  <Users className="h-3.5 w-3.5 text-blue-600" />
                  <span className="text-xs font-medium text-blue-800">
                    {conferenceParticipants.length} added
                  </span>
                </div>
                <div className="space-y-1">
                  {conferenceParticipants.map((participant) => (
                    <div key={participant.participantSid} className="flex items-center justify-between p-1.5 bg-white rounded border border-blue-100">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className={`h-2 w-2 rounded-full flex-shrink-0 ${
                          participant.status === 'connected' ? 'bg-green-500' :
                          participant.status === 'ringing' ? 'bg-yellow-500 animate-pulse' :
                          participant.status === 'hold' ? 'bg-orange-500' : 'bg-gray-400'
                        }`} />
                        <span className="text-xs truncate">{participant.name || formatPhone(participant.phoneNumber || '')}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`h-5 w-5 p-0 ${participant.isOnHold ? 'text-amber-500 hover:text-amber-600' : 'text-gray-500 hover:text-gray-600'}`}
                          onClick={() => holdParticipant?.(participant.participantSid, !participant.isOnHold)}
                          title={participant.isOnHold ? 'Resume participant' : 'Hold participant'}
                        >
                          <Pause className="h-2.5 w-2.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 p-0 text-red-500 hover:text-red-600"
                          onClick={() => removeParticipant?.(participant.participantSid)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Live Transfer panel — recommended upline owners (2026-07-23) */}
        {showTransferPanel && !pendingTransfer && (
          <div className="mb-4 p-3 border border-emerald-200 rounded-lg bg-emerald-50/60">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <PhoneForwarded className="h-4 w-4 text-emerald-600" />
                <span className="text-sm font-medium text-emerald-900">{isTransferredInCall ? 'Transfer back' : 'Transfer to'}</span>
              </div>
              <span className="text-[10px] text-emerald-700/70">{isTransferredInCall ? 'or send further up' : 'Recommended uplines'}</span>
            </div>
            {isLoadingTransferTargets ? (
              <div className="py-3 text-center text-xs text-muted-foreground">Loading uplines…</div>
            ) : transferTargets.length === 0 ? (
              <div className="py-3 text-center text-xs text-muted-foreground">No agency owners found in your upline.</div>
            ) : (
              <div className="space-y-1.5 max-h-44 overflow-y-auto">
                {transferTargets.map((t) => (
                  <button
                    key={t.userId}
                    type="button"
                    disabled={t.availability !== 'available'}
                    onClick={() => handleInitiateTransfer(t)}
                    className={`w-full flex items-center justify-between p-2 rounded border text-left transition-colors ${
                      t.availability !== 'available'
                        ? 'bg-gray-50 border-gray-200 opacity-60 cursor-not-allowed'
                        : 'bg-white border-emerald-200 hover:border-emerald-400 hover:bg-emerald-50'
                    }`}
                  >
                    <div className="min-w-0">
                      <div className="text-xs font-medium truncate">{t.name}</div>
                      <div className="text-[10px] text-muted-foreground truncate flex items-center gap-1">
                        {t.depth < 0 ? (
                          <>
                            <Undo2 className="h-2.5 w-2.5 flex-shrink-0" />
                            <span>send the call back</span>
                          </>
                        ) : (
                          <span>{t.agencyName}{t.depth === 0 ? ' · your agency' : ''}</span>
                        )}
                      </div>
                    </div>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                      t.availability === 'available' ? (t.reachableVia === 'cell' ? 'bg-sky-100 text-sky-700' : 'bg-green-100 text-green-700')
                      : t.availability === 'busy' ? 'bg-red-100 text-red-600'
                      : 'bg-gray-100 text-gray-500'
                    }`}>
                      {t.availability === 'available' ? (t.reachableVia === 'cell' ? 'Cell' : 'Available') : t.availability === 'busy' ? 'On a call' : 'Offline'}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Transfer ringing banner */}
        {pendingTransfer && (
          <div className="mb-4 p-3 border border-emerald-300 rounded-lg bg-emerald-50 flex items-start justify-between gap-2">
            <div className="flex items-start gap-2 min-w-0">
              <span className="h-2 w-2 mt-1 rounded-full bg-emerald-500 animate-pulse flex-shrink-0" />
              <span className="text-xs text-emerald-900 leading-snug">
                {pendingTransfer.isBack
                  ? `Sending the call back — ringing ${pendingTransfer.targetName}… you'll drop off when they accept`
                  : `Ringing ${pendingTransfer.targetName}… you'll drop off when they accept`}
              </span>
            </div>
            <Button size="sm" variant="ghost" className="h-6 text-xs text-red-600 hover:text-red-700 flex-shrink-0" onClick={handleCancelTransfer}>
              Cancel
            </Button>
          </div>
        )}

        {/* End Call Button */}
        <div className="flex justify-center gap-3">
          <Button
            size="lg"
            className="rounded-full h-14 w-14 bg-red-500 hover:bg-red-600 shadow-lg"
            onClick={() => disconnect({ force: true, userInitiated: true })}
          >
            <PhoneOff className="h-6 w-6" />
          </Button>
        </div>

        {/* Quick Disposition Buttons */}
        {leadId && (
          <div className="grid grid-cols-3 gap-1.5 mt-3">
            <Button
              variant="outline"
              size="sm"
              className="text-xs h-8 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
              onClick={() => handleWidgetDisposition(LeadStatus.BAD_NUMBER)}
            >
              Bad #
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-xs h-8 border-amber-200 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
              onClick={() => handleWidgetDisposition(LeadStatus.DO_NOT_CALL)}
            >
              DNC
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-xs h-8 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
              onClick={() => handleWidgetDisposition(LeadStatus.NO_SALE)}
            >
              No Sale
            </Button>
          </div>
        )}
        {leadId ? (
          <div className="mt-1.5">
            <QuickDispositionButtons onPick={handleWidgetDisposition} />
          </div>
        ) : null}

        {/* View Lead Button */}
        {leadId && (
          <Button
            variant="outline"
            className="w-full mt-4"
            onClick={() => router.push(`/leads/${leadId}`)}
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            View Lead
          </Button>
        )}
      </TabsContent>

      <TabsContent forceMount value="insurabot" className={`flex-1 flex flex-col min-h-0 mt-0 px-4 pb-4 overflow-y-auto ${callTab !== 'insurabot' ? 'hidden' : ''}`}>
        {activeCallSid ? (
          <>
            <InsuraABotKeyGrabber
              codes={insuraABotKeyCodes}
              isDTMFListening={isDTMFListening}
              setIsDTMFListening={setIsDTMFListening}
              callId={callMetadata?.parentCallSid || activeCallSid}
              isSpeechProcessing={isSpeechProcessing}
              onClearCodes={handleClearCodes}
            />
            <DTMFPollingWrapper
              ref={dtmfPollingRef}
              callSid={callMetadata?.parentCallSid || activeCallSid}
              enabled={isDTMFListening && !!activeCallSid}
              userId={user?.user?.id || null}
              onDigitsUpdate={handleDTMFUpdate}
              onSpeechProcessingUpdate={setIsSpeechProcessing}
            />
          </>
        ) : (
          <div className="text-center text-sm text-gray-500 py-8">
            InsuraBot is available during active calls
          </div>
        )}
      </TabsContent>
    </Tabs>
  );

  // Render leads queue - styled like the MultiSelectActions floating panel
  const renderQueue = () => (
    <div data-tour="dialer-queue" className="p-4 flex-1 flex flex-col overflow-hidden">
      {/* Remove every after-hours lead from the queue in one tap */}
      {/* (defined inline below via handleRemoveAfterHours) */}
      {/* TCPA calling-hours notice — how many selected leads are outside
          8am–9pm their local time and will be skipped if dialed now. */}
      {outsideHoursCount > 0 && (
        <div className="mb-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-800">
          <Clock className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-amber-600" />
          <span className="flex-1">
            <span className="font-semibold tabular-nums">{outsideHoursCount.toLocaleString()}</span>{" "}
            of {selectedLeads.length.toLocaleString()} {outsideHoursCount === 1 ? "lead is" : "leads are"} outside
            calling hours (8am–9pm their local time) and {outsideHoursCount === 1 ? "won't" : "won't"} be dialed.
            Sold, underwriting & appointment leads are exempt.
            {blockedLeadIds.size > 0 && (
              <button
                type="button"
                onClick={() => {
                  blockedLeadIds.forEach((id) => removeLeadFromDialer(String(id)));
                }}
                className="ml-1 font-semibold text-amber-900 underline underline-offset-2 hover:text-amber-950"
              >
                Remove {blockedLeadIds.size === 1 ? "it" : `all ${blockedLeadIds.size}`} from queue
              </button>
            )}
          </span>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-primary rounded-full" />
          <span className="font-semibold text-sm">
            {selectedLeads.length.toLocaleString()} lead{selectedLeads.length !== 1 ? "s" : ""} selected
          </span>
          {/* DialIQ source indicator — subtle pill telling the agent these
              leads came from the smart-redial queue. Encourages Power Dialer
              choice (where predictive runs). */}
          {dialerSource === 'dialiq' && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-violet-700 border border-violet-200 bg-violet-50/60 px-1.5 py-0.5 rounded">
              <span className="h-1 w-1 rounded-full bg-violet-500" />
              DialIQ
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                title="Bulk Actions"
                disabled={bulkUpdateStatus.isPending}
              >
                <ArrowRightLeft className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52 max-h-[400px] overflow-y-auto" sideOffset={4}>
              {/* Pipeline Section */}
              <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Move {selectedLeads.length.toLocaleString()} to Pipeline
              </div>
              {pipelineStages?.map((stage) => (
                <DropdownMenuItem
                  key={stage.id}
                  disabled={bulkUpdateStatus.isPending}
                  onClick={() => {
                    const leadIds = selectedLeads.map(l => l.id.toString());
                    const newStatus = stage.systemValue || stage.name;
                    bulkUpdateStatus.mutate(
                      { leadIds, newStatus },
                      {
                        onSuccess: () => {
                          clearSelectedLeads();
                          setCurrentView('keypad');
                        },
                      }
                    );
                  }}
                  className="cursor-pointer gap-2"
                >
                  <div
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: stage.color }}
                  />
                  <span className="text-sm truncate">{stage.name}</span>
                </DropdownMenuItem>
              ))}

              {/* Lead Source Section */}
              <div className="border-t my-1" />
              <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Set Lead Source
              </div>
              <BulkSourceOptions selectedLeads={selectedLeads} />
              <DropdownMenuItem
                onClick={async () => {
                  try {
                    const { default: srcApi } = await import("@/app/_module/services/api");
                    const { getApiUrl } = await import("@/app/_module/services/config");
                    const { showToast } = await import("@/app/_module/utils/show-toast");
                    await srcApi.post({
                      url: getApiUrl("/leads/bulk-update-source"),
                      body: { leadIds: selectedLeads.map(l => l.id), listName: "" },
                      auth: true,
                    });
                    showToast(`Cleared lead source from ${selectedLeads.length} leads`, "success");
                  } catch {}
                }}
                className="cursor-pointer gap-2 text-red-500"
              >
                <X className="h-3.5 w-3.5" />
                <span className="text-sm">Clear Source</span>
              </DropdownMenuItem>

              {/* Tags Section */}
              <div className="border-t my-1" />
              <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Add Tag
              </div>
              <BulkTagOptions selectedLeads={selectedLeads} />
              <DropdownMenuItem
                onClick={async () => {
                  try {
                    const { default: tagApi } = await import("@/app/_module/services/api");
                    const { getApiUrl } = await import("@/app/_module/services/config");
                    const { showToast } = await import("@/app/_module/utils/show-toast");
                    await tagApi.post({
                      url: getApiUrl("/leads/bulk-clear-tags"),
                      body: { leadIds: selectedLeads.map(l => l.id) },
                      auth: true,
                    });
                    showToast(`Cleared all tags from ${selectedLeads.length} leads`, "success");
                  } catch {}
                }}
                className="cursor-pointer gap-2 text-red-500"
              >
                <X className="h-3.5 w-3.5" />
                <span className="text-sm">Clear All Tags</span>
              </DropdownMenuItem>

              {bulkUpdateStatus.isPending && (
                <div className="text-center text-xs text-muted-foreground py-2">
                  Updating...
                </div>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              clearSelectedLeads();
              setCurrentView('keypad');
            }}
            className="h-6 w-6 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Selected Leads Preview */}
      <div className="mb-4 flex-1 min-h-0 flex flex-col">
        <div className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
          Selected Leads
        </div>
        <ScrollArea className="flex-1 w-full rounded-md border bg-muted/20">
          <div className="p-2 space-y-1">
            {selectedLeads.map((lead) => {
              const isAfterHours = blockedLeadIds.has(String(lead.id));
              return (
                <div
                  key={lead.id}
                  className={`flex items-center gap-2 p-2 rounded-sm border text-sm ${
                    isAfterHours ? "bg-amber-50 border-amber-200" : "bg-white"
                  }`}
                >
                  <User className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{lead.name}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {lead.phone ? formatPhone(lead.phone) : "No phone"}
                    </div>
                  </div>
                  {isAfterHours && (
                    <>
                      <span className="flex flex-shrink-0 items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">
                        <Clock className="h-2.5 w-2.5" /> After hours
                      </span>
                      <button
                        type="button"
                        onClick={() => removeLeadFromDialer(String(lead.id))}
                        title="Remove from queue"
                        className="flex-shrink-0 rounded p-0.5 text-amber-600 hover:bg-amber-100 hover:text-amber-800"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </div>

      {/* AI Area Code Matching — clickable box opens strategy popover */}
      <Popover open={isAiPopoverOpen} onOpenChange={handleAiPopoverOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="mb-4 w-full p-3 rounded-lg border bg-primary/5 flex-shrink-0 text-left hover:bg-primary/10 transition-colors cursor-pointer"
            onClick={(e) => {
              // Shell: the panel is CSS-pinned (popper drifts in WKWebView) —
              // measure the trigger so the panel's bottom sits just above it.
              const r = e.currentTarget.getBoundingClientRect();
              document.documentElement.style.setProperty(
                "--ai-pop-bottom",
                `${Math.max(90, window.innerHeight - r.top - 52)}px`,
              );
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Crosshair className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">AI Area Code Matching</span>
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-green-100 text-green-700 border-green-200">ON</Badge>
              </div>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-primary/10 text-primary border border-primary/20">
                <Settings className="h-2.5 w-2.5" />
                Configure
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-1 ml-6">
              {matchingStrategy === 'metropolitan'
                ? "Using AI Metropolitan Matching"
                : matchingStrategy === 'exact'
                ? "Using AI Exact Area Code Matching"
                : "Using AI Nearest Area Code Matching"}
            </p>
          </button>
        </PopoverTrigger>
        <PopoverContent
          side="left"
          align="end"
          sideOffset={24}
          className={`ai-match-pop w-80 p-0 overflow-hidden border-2 border-primary rounded-lg shadow-2xl shadow-primary/50 flex flex-col ${popoverView === 'recommendations' ? 'max-h-[480px]' : ''}`}
        >
          {/* Header — changes title based on view */}
          <div className="flex items-center gap-2.5 px-4 py-3 bg-gradient-to-br from-primary via-primary/90 to-primary/80 rounded-t-md flex-shrink-0">
            {popoverView === 'recommendations' && (
              <button
                type="button"
                onClick={() => setPopoverView('strategy')}
                className="p-1 rounded-md bg-white/10 hover:bg-white/20 transition-colors flex-shrink-0"
              >
                <ChevronLeft className="h-3.5 w-3.5 text-white" />
              </button>
            )}
            {popoverView === 'strategy' && (
              <div className="p-1.5 rounded-lg bg-white/10 ring-1 ring-white/20 flex-shrink-0">
                <Crosshair className="h-4 w-4 text-white" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              {popoverView === 'strategy' ? (
                <>
                  <h4 className="text-white font-bold text-sm tracking-tight">AI Area Code Matching</h4>
                  <p className="text-black text-[11px] mt-0.5 font-normal">Select your matching strategy below.</p>
                </>
              ) : (
                <>
                  <h4 className="text-white font-bold text-sm tracking-tight">Area Codes to Unlock</h4>
                  <p className="text-black text-[11px] mt-0.5 font-normal">Based on your recent call patterns</p>
                </>
              )}
            </div>
            {popoverView === 'recommendations' && !isLoadingRecs && popoverRecs.length > 0 && (
              <span className="flex-shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold bg-white/15 text-white border border-white/20">
                {popoverRecs.length}
              </span>
            )}
          </div>

          {/* STRATEGY VIEW */}
          {popoverView === 'strategy' && (
            <div className="divide-y divide-gray-100">
              {/* Nearest — always on */}
              <div className="px-4 py-3 bg-emerald-50/70">
                <div className="flex items-start gap-2.5">
                  <div className="mt-0.5 p-1.5 rounded-full bg-emerald-100 flex-shrink-0">
                    <Crosshair className="h-3 w-3 text-emerald-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-xs font-semibold text-gray-900">AI Nearest Area Code Matching</span>
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold tracking-wide bg-emerald-100 text-emerald-700 border border-emerald-200 uppercase">Always Active</span>
                    </div>
                    <p className="text-[11px] text-gray-500 mt-0.5">Default strategy. Uses geographic distance for best local appearance.</p>
                  </div>
                </div>
              </div>

              {/* Metropolitan */}
              <div className={`px-4 py-3 transition-colors ${matchingStrategy === 'metropolitan' ? 'bg-blue-50/40' : 'bg-white hover:bg-gray-50/60'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-2.5 flex-1 min-w-0">
                    <div className={`mt-0.5 p-1.5 rounded-full flex-shrink-0 transition-colors ${matchingStrategy === 'metropolitan' ? 'bg-blue-100' : 'bg-gray-100'}`}>
                      <MapPin className={`h-3 w-3 transition-colors ${matchingStrategy === 'metropolitan' ? 'text-blue-600' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <Label htmlFor="widget-metro" className="text-xs font-semibold text-gray-900 cursor-pointer">AI Metropolitan Matching</Label>
                      <p className="text-[11px] text-gray-500 mt-0.5">Matches within the same metropolitan area. Ideal for major metro regions.</p>
                    </div>
                  </div>
                  <Switch
                    id="widget-metro"
                    checked={matchingStrategy === 'metropolitan'}
                    onCheckedChange={(c) => handleWidgetStrategyToggle(c ? 'metropolitan' : 'nearest')}
                    disabled={isSavingStrategy}
                    className="flex-shrink-0 scale-90 mt-0.5"
                  />
                </div>
              </div>

              {/* Exact */}
              <div className={`px-4 py-3 transition-colors ${matchingStrategy === 'exact' ? 'bg-blue-50/40' : 'bg-white hover:bg-gray-50/60'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-2.5 flex-1 min-w-0">
                    <div className={`mt-0.5 p-1.5 rounded-full flex-shrink-0 transition-colors ${matchingStrategy === 'exact' ? 'bg-blue-100' : 'bg-gray-100'}`}>
                      <Target className={`h-3 w-3 transition-colors ${matchingStrategy === 'exact' ? 'text-blue-600' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <Label htmlFor="widget-exact" className="text-xs font-semibold text-gray-900 cursor-pointer">AI Exact Area Code Matching</Label>
                      <p className="text-[11px] text-gray-500 mt-0.5">Only matches when you have the exact same area code as the lead.</p>
                    </div>
                  </div>
                  <Switch
                    id="widget-exact"
                    checked={matchingStrategy === 'exact'}
                    onCheckedChange={(c) => handleWidgetStrategyToggle(c ? 'exact' : 'nearest')}
                    disabled={isSavingStrategy}
                    className="flex-shrink-0 scale-90 mt-0.5"
                  />
                </div>
              </div>

              {/* Active strategy footer */}
              <div className="px-4 py-2.5 bg-primary/5 flex items-center gap-2">
                <div className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse flex-shrink-0" />
                <span className="text-[11px] text-gray-500">
                  Active:{' '}
                  <span className="font-semibold text-gray-800">
                    {matchingStrategy === 'nearest' && 'AI Nearest (Default)'}
                    {matchingStrategy === 'metropolitan' && 'AI Metropolitan'}
                    {matchingStrategy === 'exact' && 'AI Exact Match'}
                  </span>
                </span>
                {isSavingStrategy && <div className="ml-auto h-2.5 w-2.5 border border-gray-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />}
              </div>

              {/* Action buttons — hidden on the shell: number management is
                  web-only there and recommendations lead to purchasing */}
              <div className="ai-pop-actions px-3 py-3 space-y-2">
                <Button size="sm" variant="outline" className="w-full h-8 text-xs border-primary/20 text-primary hover:bg-primary/5" onClick={() => { router.push("/settings?tab=area-codes"); setIsAiPopoverOpen(false); }}>
                  <Settings className="h-3.5 w-3.5 mr-1.5" />
                  Manage Numbers
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full h-8 text-xs border-primary/20 text-primary hover:bg-primary/5 gap-1.5"
                  onClick={() => setPopoverView('recommendations')}
                >
                  <Sparkles className="h-3.5 w-3.5 flex-shrink-0" />
                  <span>View Recommended Area Codes</span>
                  {isLoadingRecs ? (
                    <div className="ml-auto h-3 w-3 border border-primary border-t-transparent rounded-full animate-spin flex-shrink-0" />
                  ) : (
                    <span className={`ml-auto inline-flex items-center justify-center h-4 min-w-[1rem] px-1 rounded-full text-[9px] font-bold flex-shrink-0 ${popoverRecs.length > 0 ? 'bg-primary text-white' : 'bg-gray-100 text-gray-400'}`}>
                      {popoverRecs.length > 0 ? popoverRecs.length : '0'}
                    </span>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* RECOMMENDATIONS VIEW */}
          {popoverView === 'recommendations' && (
            <div className="flex-1 overflow-y-auto">
              {isLoadingRecs ? (
                <div className="flex flex-col items-center py-10 gap-3">
                  <div className="h-6 w-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  <span className="text-[11px] text-gray-400">Analyzing your call patterns...</span>
                </div>
              ) : popoverRecs.length === 0 ? (
                <div className="flex flex-col items-center py-10 gap-2 px-6 text-center">
                  <CheckCircle className="h-9 w-9 text-emerald-400" />
                  <span className="text-sm font-semibold text-gray-700">You&apos;re all set!</span>
                  <span className="text-[11px] text-gray-400">No high-priority area codes missing based on your recent call activity.</span>
                </div>
              ) : (
                <div className="p-3 space-y-3">
                  {(['high', 'medium', 'low'] as const).map((priority) => {
                    const items = popoverRecs.filter((r) => (r.priority ?? '').toLowerCase() === priority);
                    if (items.length === 0) return null;
                    const cfg = {
                      high:   { label: 'HIGH PRIORITY',   dot: 'bg-red-500',     left: 'border-l-red-400',     card: 'hover:border-red-200',     cost: 'text-red-600' },
                      medium: { label: 'MEDIUM PRIORITY',  dot: 'bg-amber-400',   left: 'border-l-amber-400',   card: 'hover:border-amber-200',   cost: 'text-amber-600' },
                      low:    { label: 'LOWER PRIORITY',   dot: 'bg-emerald-400', left: 'border-l-emerald-400', card: 'hover:border-emerald-200', cost: 'text-emerald-600' },
                    }[priority];
                    return (
                      <div key={priority}>
                        <div className="flex items-center gap-1.5 mb-1.5 px-0.5">
                          <div className={`h-1.5 w-1.5 rounded-full ${cfg.dot}`} />
                          <span className="text-[9px] font-black tracking-widest text-gray-400 uppercase">{cfg.label}</span>
                        </div>
                        <div className="space-y-1.5">
                          {items.map((rec) => (
                            <button
                              key={rec.areaCode}
                              type="button"
                              onClick={() => { router.push("/settings?tab=area-codes&areaCodeTab=purchase"); setIsAiPopoverOpen(false); }}
                              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-gray-100 border-l-2 ${cfg.left} bg-white ${cfg.card} hover:shadow-sm transition-all text-left group`}
                            >
                              <div className="flex-shrink-0 text-center w-10">
                                <div className="text-lg font-black text-gray-900 leading-none tracking-tight">{rec.areaCode}</div>
                                <div className="text-[9px] text-gray-400 mt-0.5 font-semibold uppercase tracking-wide">{rec.state}</div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-[11px] font-semibold text-gray-700 truncate">{rec.city || rec.state}</span>
                                  {rec.potentialMatches != null && (
                                    <span className="flex-shrink-0 px-1.5 py-px rounded-full text-[9px] font-semibold bg-gray-100 text-gray-500">{rec.potentialMatches} leads</span>
                                  )}
                                </div>
                              </div>
                              <div className="flex-shrink-0 text-right">
                                <div className={`text-xs font-black ${cfg.cost}`}>
                                  ${rec.estimatedCost}<span className="text-[9px] font-normal text-gray-400">/mo</span>
                                </div>
                                <div className="text-[9px] text-gray-300 group-hover:text-primary transition-colors mt-0.5 font-semibold">Buy →</div>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}

                  {/* Buy all CTA */}
                  <Button
                    size="sm"
                    className="w-full mt-1 h-8 text-xs bg-primary text-white hover:bg-primary/90"
                    onClick={() => { router.push("/settings?tab=area-codes&areaCodeTab=purchase"); setIsAiPopoverOpen(false); }}
                  >
                    <ShoppingCart className="h-3.5 w-3.5 mr-1.5" />
                    Buy Recommended Numbers
                  </Button>
                </div>
              )}
            </div>
          )}
        </PopoverContent>
      </Popover>

      {/* Action Buttons */}
      <div className="space-y-2 flex-shrink-0">
        {/* Dialing is capped at 500. When more are selected, lock the dial
            buttons so it's explicit — bulk actions (Move to Pipeline / Set
            Source) above still apply to ALL selected. */}
        {selectedLeads.length > 500 && (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] leading-snug text-amber-800">
            <Zap className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>
              Dialing is limited to <span className="font-semibold">500 leads</span>. You have {selectedLeads.length.toLocaleString()} selected —
              deselect down to 500 or fewer to dial. Bulk actions above still apply to all {selectedLeads.length.toLocaleString()}.
            </span>
          </div>
        )}
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                data-tour="dialer-power-dial"
                disabled={selectedLeads.length > 500}
                className={
                  dialerSource === 'dialiq'
                    ? "w-full justify-start gap-3 h-auto py-2.5 bg-white hover:bg-violet-50 text-gray-900 border border-violet-300 ring-2 ring-violet-400/30 shadow-sm"
                    : "w-full justify-start gap-3 bg-primary hover:bg-primary/90"
                }
                onClick={handleStartPowerDialer}
              >
                <Zap className={`h-4 w-4 flex-shrink-0 ${dialerSource === 'dialiq' ? 'text-violet-600' : ''}`} />
                {dialerSource === 'dialiq' ? (
                  <div className="flex flex-col items-start text-left leading-tight">
                    <span className="flex items-center gap-1.5">
                      <span className="text-gray-900 font-semibold">Start Power Dial Session</span>
                      <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.14em] font-bold bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded border border-violet-200">
                        <span className="h-1 w-1 rounded-full bg-violet-500 animate-pulse" />
                        Predictive
                      </span>
                    </span>
                    <span className="text-[10px] font-normal text-gray-500 mt-0.5">
                      DialIQ pre-dials the next lead while you talk
                    </span>
                  </div>
                ) : (
                  <>Start Power Dial Session</>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left" className="max-w-[240px]">
              <p className="text-xs">
                {dialerSource === 'dialiq'
                  ? 'DialIQ predictive mode: the next lead is dialed during your current call and bridged the moment you hang up. Zero dead time.'
                  : 'Dials one lead at a time, auto-advances instantly — fastest way to burn through your list'}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                data-tour="dialer-multiline"
                disabled={selectedLeads.length > 500}
                className="w-full justify-start gap-3 border-primary/20 hover:bg-primary/5"
                onClick={handleStartMultiLineDialer}
                variant="outline"
              >
                <Users className="h-4 w-4" />
                Start Multi-Line Session
                {isBasicPlanUser && (
                  <span className="ml-auto text-[10px] font-semibold text-muted-foreground border rounded px-1.5 py-0.5">
                    Full-Fledged+
                  </span>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left" className="max-w-[220px]">
              <p className="text-xs">
                {isBasicPlanUser
                  ? "Multi-Line requires the Full-Fledged plan or higher. Your plan includes Power Dialer."
                  : "Rings up to 4 leads at once and connects you to the first one who answers. Not as fast as power dial but you talk to more leads"}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <Button
          className="w-full justify-start gap-3 border-red-200 hover:bg-red-50 text-red-600"
          onClick={() => {
            if (!requestDeleteSelectedLeads()) {
              toast({ title: 'Open the Leads page to delete', description: 'Selected leads can only be deleted from the Leads page, where the confirmation shows.' });
            }
          }}
          variant="outline"
        >
          <Trash2 className="h-4 w-4" />
          Delete Selected Leads
        </Button>
      </div>
    </div>
  );

  // Check if we should show lead details (full number entered and lead found)
  const showLeadDetails = cleanedDialNumber.length === 10 && matchedLead;

  // Render keypad view
  const renderKeypad = () => (
    <div data-tour="dialer-keypad-button" className="p-3 flex flex-col flex-1 min-h-0 overflow-hidden">
      {/* AI Matching ON/OFF — manual dialer only (power dialer / multi-line always match) */}
      <div className="mb-2 p-1.5 rounded-lg border border-gray-200 bg-gray-50/50 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <Crosshair className="h-3.5 w-3.5 text-primary" />
            <Label htmlFor="ai-matching" className="text-[11px] font-medium cursor-pointer">
              AI Matching {manualAiMatching ? 'ON' : 'OFF'}
            </Label>
          </div>
          <Switch
            id="ai-matching"
            checked={manualAiMatching}
            onCheckedChange={(checked) => {
              setManualAiMatching(checked);
              if (typeof window !== 'undefined') {
                localStorage.setItem('dialerAiMatchingEnabled', JSON.stringify(checked));
              }
            }}
            className="scale-90"
          />
        </div>
      </div>
      {/* AI matching OFF: the call goes out from this number */}
      {!manualAiMatching && (
        <div className="mb-2 flex-shrink-0">
          <AgentPhoneSelector
            selectedPhoneNumber={selectedAgentPhone}
            onPhoneNumberSelect={(phoneNumber) => setSelectedAgentPhone(phoneNumber)}
            placeholder="Select your number"
            showStateInline={true}
            activeOnly={true}
            autoSelectDefault={true}
          />
        </div>
      )}

      {/* Phone Input with Delete Button */}
      <div className="mb-2 flex items-center gap-2 flex-shrink-0">
        <Input
          ref={inputRef}
          type="tel"
          value={formatDialDisplay(dialNumber)}
          onChange={handlePhoneInputChange}
          placeholder="(XXX) XXX-XXXX"
          className="text-center text-lg font-mono h-10 border-gray-200 focus:border-blue-500 flex-1"
          maxLength={14}
        />
        {dialNumber && (
          <Button
            variant="outline"
            size="sm"
            className="rounded-full h-10 w-10 flex-shrink-0"
            onClick={handleDialBackspace}
          >
            <Delete className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Lead Details Card - Show when full number entered and lead found */}
      {showLeadDetails ? (
        <div className="flex-1 flex flex-col min-h-0">
          {/* Lead Info Card */}
          <div className="flex-1 rounded-xl border border-gray-200 bg-white shadow-sm flex flex-col overflow-hidden">
            {/* Header with name and status */}
            <div className="px-3 py-2.5 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-100">
              <div className="flex items-center gap-2.5">
                <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 text-white">
                  <User className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900 truncate text-sm">{matchedLead.name}</h3>
                    {(matchedLead.leadStat || matchedLead.status) && (
                      <Badge variant="secondary" className="text-[9px] px-1.5 py-0 h-4 bg-blue-100 text-blue-700 border-0">
                        {getStatusDisplayName(matchedLead.leadStat || matchedLead.status)}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">{formatPhone(matchedLead.phone || '')}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-gray-400 hover:text-blue-600 hover:bg-blue-50 flex-shrink-0"
                  onClick={() => router.push(`/leads/${matchedLead.id}`)}
                  title="View Lead Details"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>

            {/* Lead Details - Clean list */}
            <div className="flex-1 px-3 py-2 space-y-1 overflow-y-auto">
              {/* Location row */}
              {(matchedLead.city || matchedLead.state) && (
                <div className="flex items-center gap-1.5 text-xs text-gray-600">
                  <MapPin className="h-3 w-3 text-gray-400 flex-shrink-0" />
                  <span>{[matchedLead.city, matchedLead.state].filter(Boolean).join(', ')}</span>
                </div>
              )}

              {/* Email */}
              {matchedLead.email && (
                <div className="flex items-center gap-1.5 text-xs text-gray-600">
                  <Mail className="h-3 w-3 text-gray-400 flex-shrink-0" />
                  <span className="truncate">{matchedLead.email}</span>
                </div>
              )}

              {/* Age, DOB, Gender row */}
              {(matchedLead.age || matchedLead.dob || matchedLead.gender) && (
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <Calendar className="h-3 w-3 text-gray-400 flex-shrink-0" />
                  <span>
                    {matchedLead.age ? `${matchedLead.age} yrs` : ''}
                    {matchedLead.dob ? (() => {
                      // Parse date as UTC to avoid timezone offset issues
                      const [year, month, day] = matchedLead.dob.split('T')[0].split('-').map(Number);
                      const date = new Date(year, month - 1, day);
                      return ` (${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })})`;
                    })() : ''}
                    {matchedLead.gender ? ` • ${matchedLead.gender.charAt(0).toUpperCase()}` : ''}
                  </span>
                </div>
              )}

              {/* Marital & Employment */}
              {(matchedLead.marital || matchedLead.maritalStatus || matchedLead.employer) && (
                <div className="flex items-center gap-1.5 text-xs text-gray-600">
                  <Users className="h-3 w-3 text-gray-400 flex-shrink-0" />
                  <span className="truncate">
                    {(matchedLead.marital || matchedLead.maritalStatus) && (
                      <span className="capitalize">{matchedLead.marital || matchedLead.maritalStatus}</span>
                    )}
                    {matchedLead.employer && ` • ${matchedLead.employer}`}
                  </span>
                </div>
              )}

              {/* Lead List/Source */}
              {matchedLead.listName && (
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <Zap className="h-3 w-3 text-gray-400 flex-shrink-0" />
                  <span className="truncate">List: {matchedLead.listName}</span>
                </div>
              )}

              {/* Separator for call info */}
              {(matchedLead.lastCallAt || matchedLead.lastCall || matchedLead.assigned || matchedLead.createdAt) && (
                <div className="border-t border-gray-100 pt-1 mt-1 space-y-1">
                  {/* Last call with proper formatting */}
                  {(matchedLead.lastCallAt || matchedLead.lastCall) && (
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <Phone className="h-3 w-3 text-gray-400 flex-shrink-0" />
                      <span>
                        Last call: {(() => {
                          const dateValue = matchedLead.lastCallAt || matchedLead.lastCall;
                          if (!dateValue) return 'N/A';
                          const date = new Date(dateValue);
                          if (isNaN(date.getTime())) return String(dateValue);
                          // Format: Dec 29, 2025, 09:33 PM
                          const month = date.toLocaleString('en-US', { month: 'short' });
                          const day = date.getDate();
                          const year = date.getFullYear();
                          const hours = date.getHours();
                          const minutes = date.getMinutes().toString().padStart(2, '0');
                          const period = hours >= 12 ? 'PM' : 'AM';
                          const hour12 = (hours % 12 || 12).toString().padStart(2, '0');
                          return `${month} ${day}, ${year}, ${hour12}:${minutes} ${period}`;
                        })()}
                        {matchedLead.longestCall && ` (${matchedLead.longestCall}s)`}
                      </span>
                    </div>
                  )}

                  {/* Assigned to */}
                  {matchedLead.assigned && (
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <UserCheck className="h-3 w-3 text-gray-400 flex-shrink-0" />
                      <span className="truncate">Assigned: {matchedLead.assigned}</span>
                    </div>
                  )}

                  {/* Created date */}
                  {matchedLead.createdAt && (
                    <div className="flex items-center gap-1.5 text-xs text-gray-400">
                      <Clock className="h-3 w-3 flex-shrink-0" />
                      <span>Created: {new Date(matchedLead.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Clear button - subtle at bottom */}
            <button
              className="w-full py-1.5 text-[10px] text-gray-400 hover:text-gray-600 hover:bg-gray-50 border-t border-gray-100 transition-colors"
              onClick={() => setDialNumber('')}
            >
              Clear & Show Keypad
            </button>
          </div>

          {/* Call Button - Large and prominent */}
          <Button
            className="w-full mt-3 h-14 bg-green-500 hover:bg-green-600 text-white text-lg font-semibold rounded-xl shadow-lg"
            onClick={handleManualCall}
            disabled={isDialing || !isDeviceReady || isCheckingPhoneNumbers}
          >
            {isDialing || isCheckingPhoneNumbers ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Calling...
              </>
            ) : (
              <>
                <Phone className="h-5 w-5 mr-2" />
                Call {matchedLead.name?.split(' ')[0] || 'Lead'}
              </>
            )}
          </Button>
        </div>
      ) : cleanedDialNumber.length === 10 && !matchedLead ? (
        /* Unknown Number - No lead found - styled like Power Dialer lead card */
        <div className="flex-1 flex flex-col min-h-0">
          {/* Unknown Number Card - styled like lead card */}
          <div className="flex-1 rounded-xl border border-gray-200 bg-white shadow-sm flex flex-col overflow-hidden">
            {/* Header with avatar and number */}
            <div className="px-3 py-2.5 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-100">
              <div className="flex items-center gap-2.5">
                <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 text-white">
                  <User className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900 truncate text-sm">Unknown Number</h3>
                  </div>
                  <p className="text-xs text-gray-500">{formatDialDisplay(dialNumber)}</p>
                </div>
              </div>
            </div>

            {/* Info message and Add Lead form */}
            <div className="flex-1 flex flex-col items-center justify-center p-4 gap-3">
              {!showAddLeadForm ? (
                <>
                  <p className="text-xs text-gray-400 text-center">No lead found with this number</p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-2"
                    onClick={() => setShowAddLeadForm(true)}
                  >
                    <UserPlus className="h-4 w-4" />
                    Add Lead
                  </Button>
                </>
              ) : (
                <div className="w-full space-y-3">
                  <div className="space-y-1.5">
                    <label className="text-xs text-gray-500 font-medium">Lead Name</label>
                    <Input
                      placeholder="Enter name..."
                      value={newLeadName}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => setNewLeadName(e.target.value)}
                      className="h-9 text-sm"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newLeadName.trim().length >= 2) {
                          handleCreateLeadFromDialer();
                        }
                      }}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="flex-1"
                      onClick={() => {
                        setShowAddLeadForm(false);
                        setNewLeadName("");
                      }}
                      disabled={isCreatingLead}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      className="flex-1 gap-1.5"
                      onClick={handleCreateLeadFromDialer}
                      disabled={isCreatingLead || newLeadName.trim().length < 2}
                    >
                      {isCreatingLead ? (
                        <>
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        <>
                          <UserPlus className="h-3.5 w-3.5" />
                          Create
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Clear button - subtle at bottom */}
            <button
              className="w-full py-1.5 text-[10px] text-gray-400 hover:text-gray-600 hover:bg-gray-50 border-t border-gray-100 transition-colors"
              onClick={() => setDialNumber('')}
            >
              Clear & Show Keypad
            </button>
          </div>

          {/* Call Button for unknown number */}
          <Button
            className="w-full mt-3 h-14 bg-green-500 hover:bg-green-600 text-white text-lg font-semibold rounded-xl shadow-lg"
            onClick={handleManualCall}
            disabled={isDialing || !isDeviceReady || isCheckingPhoneNumbers}
          >
            {isDialing || isCheckingPhoneNumbers ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Calling...
              </>
            ) : (
              <>
                <Phone className="h-5 w-5 mr-2" />
                Call Number
              </>
            )}
          </Button>
        </div>
      ) : (
        /* Number Pad - Show when number is incomplete */
        <>
          <div className="grid grid-cols-3 gap-1.5 flex-1 min-h-0">
            {DIAL_PAD_DIGITS.map(({ digit, letters }) => (
              <button
                key={digit}
                type="button"
                className="rounded-lg bg-gray-50 hover:bg-gray-100 active:bg-gray-200 border border-gray-200 transition-all duration-150 flex flex-col items-center justify-center shadow-sm hover:shadow active:scale-[0.98]"
                onClick={() => handleDialPadPress(digit)}
              >
                <span className="text-xl font-semibold text-gray-800">{digit}</span>
                {letters && (
                  <span className="text-[9px] text-gray-400 tracking-wider">{letters}</span>
                )}
              </button>
            ))}
          </div>

          {/* Call Button - Full width rectangle, disabled until 10 digits */}
          <Button
            className="w-full mt-2 h-12 bg-gray-300 hover:bg-gray-300 text-gray-500 text-base font-medium rounded-lg shadow-md cursor-not-allowed flex-shrink-0"
            disabled
          >
            <Phone className="h-5 w-5 mr-2" />
            Call
          </Button>

          {/* Call Again - redial last number */}
          {hasMounted && lastDialedNumber && !activeCallSid && (
            <Button
              variant="outline"
              className="w-full mt-1.5 h-9 text-xs text-gray-600 border-gray-200 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 flex-shrink-0"
              onClick={() => setDialNumber(lastDialedNumber)}
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
              Redial {lastDialedLeadName || formatDialDisplay(lastDialedNumber)}
            </Button>
          )}
        </>
      )}

      {!isDeviceReady && (
        <div className="mt-1 text-center text-[10px] text-amber-600 flex-shrink-0">
          {isReconnecting
            ? 'Reconnecting — calls disabled until green'
            : deviceError
              ? 'Disconnected — tap Reconnect above'
              : 'Initializing phone system...'}
        </div>
      )}

      {isRecoveringCall && (
        <div className="mt-1 text-center text-[10px] text-blue-600 flex-shrink-0 flex items-center justify-center gap-1">
          <span className="animate-pulse">●</span>
          Reconnecting to active call...
        </div>
      )}

      {/* Show queue button if leads are selected */}
      {selectedLeads.length > 0 && (
        <Button
          variant="outline"
          size="sm"
          className="w-full mt-2 text-blue-600 border-blue-200 h-8 text-xs flex-shrink-0"
          onClick={() => setCurrentView('queue')}
        >
          <Users className="h-3.5 w-3.5 mr-1.5" />
          View {selectedLeads.length} Queued Lead{selectedLeads.length !== 1 ? 's' : ''}
        </Button>
      )}
    </div>
  );

  // Render Power Dialer Session (embedded - uses existing component)
  // Use a stable key based on leads length to prevent unnecessary remounts during navigation
  const powerDialerSessionKey = `power-dialer-${selectedLeads.length}`;
  const renderPowerDialerSession = () => (
    <div className="flex-1 overflow-y-auto">
      <PowerDialerSession
        key={powerDialerSessionKey}
        leads={selectedLeads}
        aiMatchingEnabled={aiMatchingEnabled}
        onStop={handleStopDialer}
        onComplete={handleDialerComplete}
        hideLeadDetails={true}
      />
    </div>
  );

  // Render Multi-Line Dialer Session (embedded - uses existing component with compact widget view)
  const renderMultiLineSession = () => (
    <div className="flex-1 overflow-y-auto">
      <MultiLineDialerSession
        leads={selectedLeads}
        aiMatchingEnabled={aiMatchingEnabled}
        onStop={handleStopDialer}
        onComplete={handleDialerComplete}
        hideLeadDetails={true}
        resumeOffset={resumeOffset}
        totalLeadsOriginal={resumeOffset > 0 ? selectedLeads.length + resumeOffset : undefined}
      />
    </div>
  );

  // Format seconds to mm:ss display
  const formatDialTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Render Session Complete/Stopped View with stats
  const renderSessionComplete = () => {
    return (
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Close button to dismiss and return to keypad */}
        <div className="flex justify-end px-3 pt-2">
          <button
            type="button"
            onClick={handleDismissSessionComplete}
            className="p-1 rounded-md hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-5 pb-5 flex flex-col items-center text-center">
          {sessionWasStopped ? (
            <>
              <Pause className="h-12 w-12 text-amber-500 mb-3" />
              <h3 className="text-lg font-semibold mb-1">Session Stopped</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Called {sessionStats.called} out of {sessionStats.total} leads. Resume anytime from Session History.
              </p>
            </>
          ) : (
            <>
              <CheckCircle className="h-12 w-12 text-green-500 mb-3" />
              <h3 className="text-lg font-semibold mb-1">Session Complete!</h3>
              <p className="text-sm text-muted-foreground mb-4">
                All {sessionStats.total} lead{sessionStats.total !== 1 ? 's' : ''} have been processed.
              </p>
            </>
          )}

          {/* Stats Grid - Basic */}
          <div className="grid grid-cols-2 gap-2 w-full max-w-xs mb-2">
            <div className="bg-green-50 rounded-lg p-2.5">
              <div className="text-xl font-bold text-green-600">{sessionStats.answered}</div>
              <div className="text-[10px] text-green-600">Answered</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-2.5">
              <div className="text-xl font-bold text-blue-600">{formatDialTime(sessionStats.totalDialTime)}</div>
              <div className="text-[10px] text-blue-600">On the Dialer</div>
            </div>
          </div>

          {/* Same split as the agency dashboard's Dial & Talk Time tile, so an
              agent and their owner are reading one vocabulary. "On the dialer"
              above is this session's wall clock; these two say how much of it
              was a live call, and how much of THAT was long enough to be a real
              conversation (60s+, the dashboard's threshold). The remainder is
              voicemail pickups and instant hang-ups — the line opened, nobody
              spoke. "Machines" was the original wording and read as jargon. */}
          {sessionStats.talkSeconds > 0 && (
            <div className="w-full max-w-xs mb-2 rounded-lg border border-gray-100 bg-white px-2.5 py-2">
              <div className="flex items-baseline justify-between">
                <span className="text-[10px] text-gray-500">Talking</span>
                <span className="text-xs font-semibold text-gray-800">
                  {formatDialTime(sessionStats.talkSeconds)}
                </span>
              </div>
              <div className="mt-1 flex items-baseline justify-between pl-2">
                <span className="text-[10px] text-gray-400">— Real conversations (1 min+)</span>
                <span className="text-[11px] font-medium text-gray-600">
                  {formatDialTime(sessionStats.conversationSeconds)}
                </span>
              </div>
              <div className="mt-0.5 flex items-baseline justify-between pl-2">
                <span className="text-[10px] text-gray-400">— Voicemails &amp; hang-ups</span>
                <span className="text-[11px] font-medium text-gray-600">
                  {formatDialTime(Math.max(0, sessionStats.talkSeconds - sessionStats.conversationSeconds))}
                </span>
              </div>
            </div>
          )}

          {/* Outcome Stats - Always show all 3 */}
          <div className="grid grid-cols-3 gap-2 w-full max-w-xs">
            <div className="bg-cyan-50 rounded-lg p-2">
              <div className="text-base font-bold text-cyan-600">{sessionStats.appointments}</div>
              <div className="text-[10px] text-cyan-600">Appt</div>
            </div>
            <div className="bg-amber-50 rounded-lg p-2">
              <div className="text-base font-bold text-amber-600">{sessionStats.underwriting}</div>
              <div className="text-[10px] text-amber-600">UWR</div>
            </div>
            <div className="bg-emerald-50 rounded-lg p-2">
              <div className="text-base font-bold text-emerald-600">{sessionStats.sold}</div>
              <div className="text-[10px] text-emerald-600">Sold</div>
            </div>
          </div>
        </div>

        {/* Pinned bottom button — always visible */}
        <div className="flex-shrink-0 p-3 border-t border-gray-100">
          {sessionWasStopped ? (
            <Button
              onClick={() => {
                handleDismissSessionComplete();
                setCurrentView('history');
              }}
              className="w-full"
            >
              <Clock className="h-4 w-4 mr-2" />
              View Session History
            </Button>
          ) : (
            <Button onClick={handleDismissSessionComplete} className="w-full">
              Done
            </Button>
          )}
        </div>
      </div>
    );
  };

  // Get header title based on view
  const getHeaderTitle = () => {
    if (hasActiveCall) return 'Active Call';
    if (showSessionComplete) return sessionWasStopped ? 'Session Stopped' : 'Session Complete';
    switch (currentView) {
      case 'powerdial':
        return 'Power Dialer';
      case 'multiline':
        return 'Multi-Line Dialer';
      case 'queue':
        return 'Lead Queue';
      case 'history':
        return 'Session History';
      default:
        return 'Manual Dialer';
    }
  };

  // ── Animated Call Icon — crossfades between ringing handset and the agent on a call ──
  //
  // Ringing: the plain handset. Connected: the agent, phone to ear, talking (slow head
  // nod + speech arcs). Only the connected state animates, so the switch actually means
  // "this call is live" instead of being ambient decoration.
  //
  // COLOUR: every shape derives from `color` via currentColor + color-mix, so the whole
  // icon follows Twilio device state — green ready, amber connecting, orange
  // reconnecting, red on device error. (This previously hardcoded #38bdf8 for the
  // handset, which meant the icon couldn't reflect call state at all.)
  const AnimatedCallIcon = ({
    status,
    color,
    size = 24,
  }: { status: string; color: string; size?: number }) => {
    const isConnected = status === 'answered' || status === 'in-progress';

    // One prop drives four tones; shades stay in-family for whatever colour is passed.
    const base = 'currentColor';
    const mid = 'color-mix(in srgb, currentColor, #000 22%)';
    const dark = 'color-mix(in srgb, currentColor, #000 42%)';
    const lit = 'color-mix(in srgb, currentColor, #fff 45%)';

    return (
      <span
        className="relative inline-flex items-center justify-center"
        style={{ width: size, height: size, color }}
      >
        {/* Ringing — plain handset */}
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          className="absolute inset-0 transition-opacity duration-500"
          style={{ opacity: isConnected ? 0 : 1 }}
          aria-hidden="true"
        >
          <path
            d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>

        {/* Connected — agent with phone to ear, talking */}
        <svg
          width={size}
          height={size}
          viewBox="0 0 48 48"
          fill="none"
          className="absolute inset-0 transition-opacity duration-500"
          style={{ opacity: isConnected ? 1 : 0 }}
          role="img"
          aria-label="Call connected"
        >
          {/* speech arcs */}
          <g className="ic-agent-say">
            <path d="M36.9 13.4a5.6 5.6 0 0 1 0 7.9" stroke={lit} strokeWidth="2.2" strokeLinecap="round" />
            <path d="M40.7 9.8a10 10 0 0 1 0 15.1" stroke={lit} strokeWidth="2.2" strokeLinecap="round" />
          </g>

          {/* head + handset (nods together) */}
          <g className="ic-agent-nod">
            <circle cx="19" cy="14" r="8" fill={base} />
            {/* terminator shadow + rim highlight so it reads as a sphere */}
            <path d="M19 6a8 8 0 0 1 6.5 12.6A8 8 0 0 0 19 6z" fill={dark} opacity="0.45" />
            <circle cx="15.9" cy="10.9" r="2.7" fill={lit} opacity="0.5" />
            <g transform="rotate(18 29.4 16.3)">
              <rect x="26.3" y="10.4" width="6.2" height="11.8" rx="2" fill={dark} />
              <rect x="27.35" y="12.3" width="4.1" height="8.2" rx="0.9" fill={lit} />
              <rect x="28.15" y="11.2" width="2.5" height="0.72" rx="0.36" fill={lit} opacity="0.8" />
            </g>
          </g>

          {/* shoulders, darker on the shadow side */}
          <path d="M5 42v-3.6C5 32.6 11.3 27.4 19 27.4s14 5.2 14 11v3.6z" fill={mid} />
          <path d="M19 27.4c7.7 0 14 5.2 14 11V42h-5.4v-3.6c0-4.9-3.4-8.8-8.6-10.2z" fill={dark} opacity="0.45" />
        </svg>
      </span>
    );
  };

  // Get header icon based on view mode (idle state only)
  const getHeaderIcon = () => {
    switch (currentView) {
      case 'powerdial':
        return <Zap className="h-5 w-5" />;
      case 'multiline':
        return <Grid3X3 className="h-5 w-5" />;
      default:
        return <Phone className="h-5 w-5" />;
    }
  };

  // Determine widget width based on view - responsive for smaller screens
  const getWidgetWidth = () => {
    if (currentView === 'powerdial' || currentView === 'multiline') {
      // On smaller screens, use full width with max-width
      return 'w-[calc(100vw-2rem)] max-w-[480px] lg:w-[480px]';
    }
    return 'w-[calc(100vw-2rem)] max-w-[400px] lg:w-[400px]';
  };

  // Get widget position classes - always position on the right
  const getPositionClasses = () => {
    return 'right-4'; // Bottom right corner
  };

  // Main widget render - styled like InsuraBot
  // Render both minimized button and full widget to preserve session state
  return (
    <>
      {/* Live-transfer offer card (owner side) — also rendered by the
          !isWidgetOpen early-return above so it shows either way. */}
      {transferOfferCard}

      {/* DialIQ — threshold toast (hidden until feature is ready) */}
      {DIAL_IQ_ENABLED && <DialiqThresholdListener />}

      {/* Minimized state — hide when:
          - both InsuraBot and SMS widget are open (avoid overlap)
          - InsuraBot is open with no active call/session (the dialer icon
            now lives in the InsuraBot header). We still keep the floating
            pill visible during an active call / eavesdrop session so the
            agent has a visible status indicator. */}
      {/* ⚠️ Never on the native shell: MobileBottomNav already has a raised
          dial button, so this desktop pill is redundant AND mispositioned —
          its right/left values are computed against desktop widget rects and
          on a phone it lands stuck to the left edge over the content. */}
      {isMinimized && !(isInsuraBotOpen && activeSmsConversation) && !(isInsuraBotOpen && !hasActiveCall && !sessionActive && !eavesdropActive) &&
        // Shell: only while something is LIVE (call / session / eavesdrop) —
        // the idle pill is redundant next to the dock's dial button. Position
        // above the dock on the right, no desktop rect math.
        (!isMobileShell || hasActiveCall || sessionActive || eavesdropActive) && (
        <div
          className={`fixed z-50 ${isMobileShell ? "right-3" : `bottom-4 ${!(isInsuraBotOpen && insuraBotRect) ? 'right-4' : ''}`}`}
          style={isMobileShell
            ? { bottom: "calc(env(safe-area-inset-bottom, 0px) + 108px)" }
            : (isInsuraBotOpen && insuraBotRect ? { right: insuraBotRect.right + insuraBotRect.width + 8 } : undefined)}
        >
          {/* Eavesdrop / coach pill — shown when agency owner is monitoring.
              Color-coded by mode so the manager can tell at a glance
              whether they're stealth (red), whispering (blue), or barging
              (amber) without expanding the widget. */}
          {eavesdropActive ? (() => {
            const isWhisperPill = eavesdropMode === 'whisper';
            const isBargePill = eavesdropMode === 'barge';
            const pillBorder = isBargePill ? 'border-amber-500 shadow-amber-500/30'
              : isWhisperPill ? 'border-blue-500 shadow-blue-500/30'
              : 'border-red-500 shadow-red-500/30';
            const pillDot = isBargePill ? 'bg-amber-500' : isWhisperPill ? 'bg-blue-500' : 'bg-red-500';
            const pillIconColor = isBargePill ? 'text-amber-500' : isWhisperPill ? 'text-blue-500' : 'text-red-500';
            const PillIcon = isBargePill ? Megaphone : isWhisperPill ? Mic : Headphones;
            return (
              <div
                onClick={maximizeWidget}
                className={`flex items-center gap-2 px-3 py-2 bg-background border-2 ${pillBorder} rounded-full shadow-2xl cursor-pointer select-none animate-pulse`}
              >
                <span className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${pillDot}`} />
                <PillIcon className={`h-3.5 w-3.5 ${pillIconColor}`} />
                <span className="text-sm font-medium truncate max-w-[120px]">{eavesdropAgentName}</span>
              </div>
            );
          })() : (hasActiveCall || sessionActive) ? (
            <div
              ref={floatingBarRef}
              onMouseDown={onFloatingBarDragStart}
              className="flex items-center gap-2 px-3 py-2 bg-background border-2 border-primary rounded-full shadow-2xl shadow-primary/30 cursor-grab active:cursor-grabbing select-none"
            >
              {/* Status dot */}
              <span className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${
                hasActiveCall ? 'bg-green-500 animate-pulse' : 'bg-orange-400 animate-pulse'
              }`} />

              {/* Lead name / session info — clickable to expand */}
              <button
                onClick={maximizeWidget}
                className="flex items-center gap-1.5 min-w-0 hover:opacity-80 transition-opacity"
              >
                {hasActiveCall ? (
                  (() => {
                    // In multi-line, lines start "hasActiveCall=true" the moment
                    // they begin ringing. We don't want to leak a specific lead
                    // name before anyone picks up (and in a race-to-answer, that
                    // name could change). Show generic "Multi-Line · Dialing…"
                    // until the call is actually connected.
                    const isConnected = callStatus === 'in-progress' || callStatus === 'answered';
                    const isMultiLinePreConnect = dialerMode === 'multiline' && !isConnected;
                    if (isMultiLinePreConnect) {
                      return (
                        <>
                          <Users className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />
                          <span className="text-sm font-medium truncate max-w-[120px]">Multi-Line</span>
                          <span className="text-xs text-muted-foreground">Dialing…</span>
                        </>
                      );
                    }
                    return (
                      <>
                        <span className="text-sm font-medium truncate max-w-[120px]">
                          {callMetadata?.leadName || activeCallLead?.name || 'Unknown'}
                        </span>
                        <span className="text-xs text-muted-foreground tabular-nums">
                          {formatDuration(callDuration)}
                        </span>
                      </>
                    );
                  })()
                ) : (
                  <>
                    {dialerMode === 'power' && <Zap className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />}
                    {dialerMode === 'multiline' && <Users className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />}
                    <span className="text-sm font-medium truncate max-w-[100px]">
                      {dialerMode === 'power' ? 'Power' : 'Multi-Line'}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {currentLeadIndex + 1}/{selectedLeads.length}
                    </span>
                  </>
                )}
              </button>

              {/* Divider */}
              <div className="h-5 w-px bg-border flex-shrink-0" />

              {/* Call controls — shown when there's an active call */}
              {hasActiveCall && !isBridgedCall && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`h-8 w-8 p-0 rounded-full ${isMuted ? 'bg-red-500 text-white hover:bg-red-600' : 'hover:bg-gray-100'}`}
                    onClick={() => isMuted ? unmute() : mute()}
                    title={isMuted ? 'Unmute' : 'Mute'}
                  >
                    {isMuted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                  </Button>

                  <Button
                    variant="ghost"
                    size="sm"
                    className={`h-8 w-8 p-0 rounded-full ${isOnHold ? 'bg-amber-500 text-white hover:bg-amber-600' : 'hover:bg-gray-100'}`}
                    onClick={() => isOnHold ? unhold() : hold()}
                    title={isOnHold ? 'Resume' : 'Hold'}
                  >
                    {isOnHold ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
                  </Button>

                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 rounded-full bg-red-500 text-white hover:bg-red-600"
                    onClick={() => {
                      // For multi-line, calling disconnect() alone only ends
                      // the agent's browser leg — the customer stays on the
                      // line because the conference has endConferenceOnExit:
                      // false on the agent's participant. Dispatch an event
                      // so MultiLineDialerSession runs its proper end-line
                      // flow (POST /end-line for the connected line) which
                      // hangs up the customer cleanly. Power/manual dialer
                      // is a single call so plain disconnect() is enough.
                      if (sessionActive && dialerMode === 'multiline') {
                        window.dispatchEvent(new CustomEvent('multiline:end-current-call'));
                      } else {
                        disconnect();
                      }
                    }}
                    title="End Call"
                  >
                    <PhoneOff className="h-4 w-4" />
                  </Button>
                </>
              )}

              {/* Stop button — shown for dialer sessions between calls (no active call) */}
              {!hasActiveCall && sessionActive && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 rounded-full text-red-500 hover:bg-red-50 hover:text-red-600 text-xs font-medium"
                  onClick={handleStopDialer}
                  title="Stop Session"
                >
                  Stop
                </Button>
              )}

              {/* Expand button */}
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 rounded-full hover:bg-gray-100"
                onClick={maximizeWidget}
                title="Expand Dialer"
              >
                <ChevronUp className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            /* Default minimized circle button — no active call or session */
            <Button
              data-tour="dialer-minimized-button"
              onClick={maximizeWidget}
              className="h-12 w-12 p-0 shadow-lg relative bg-primary hover:bg-primary/90 text-primary-foreground rounded-full flex items-center justify-center"
            >
              <Phone className="h-6 w-6" />
              {selectedLeads.length > 0 && (
                <Badge className="absolute -top-1 -right-1 h-5 min-w-5 px-1 bg-red-500 text-white text-[10px] border-2 border-white">
                  {selectedLeads.length}
                </Badge>
              )}
            </Button>
          )}
        </div>
      )}

      {/* Full widget - hidden when minimized but kept mounted to preserve session state */}
      {/* Responsive height: uses max-height with vh on smaller screens to prevent overflow */}
      {/* Native shell: frost the strip of page visible around the dialer sheet
          (above the tab bar / below the header). Tap = minimize, same as the
          chevron. z-40 sits under the sheet (z-50) and over everything else. */}
      {/* UNMOUNT when minimized — this WKWebView composites backdrop-filter
          even at opacity 0, so the always-mounted variant dimmed+blurred the
          ENTIRE app permanently ("why does the whole app feel dimmer"). */}
      {isMobileShell && !isMinimized && (
        <div
          className="m-dialer-scrim fixed inset-0 z-40 bg-black/20"
          onClick={() => minimizeWidget()}
        />
      )}
      <div
        data-tour="dialer-widget"
        // ⚠️ On the native app the widget becomes a full sheet via the
        // .mobile-dialer class — which existed in globals.css doing exactly the
        // right thing (safe-area insets, clears the header AND the bottom tab
        // bar) and was never applied to anything. Without it the desktop
        // geometry (bottom-2, 600px tall) painted OVER the tab bar and the
        // home indicator, trapping users inside the dialer.
        // Mobile keeps the sheet MOUNTED when minimized (mobile-dialer--closed
        // translates it off-screen) so open/close both run the Apple-sheet
        // transition; display:none would snap. Desktop behavior unchanged.
        className={`${isMobileShell
          ? `mobile-dialer z-50 bg-background border border-black/10 rounded-xl shadow-2xl flex flex-col ${isMinimized ? 'mobile-dialer--closed' : ''}`
          : `fixed bottom-2 ${getPositionClasses()} z-50 ${getWidgetWidth()} h-[600px] max-h-[calc(100vh-6rem)] lg:h-[600px] lg:max-h-[600px] bg-background border-2 border-primary rounded-lg shadow-primary/50 shadow-2xl flex flex-col animate-in slide-in-from-bottom-4 duration-300 ${isMinimized ? 'hidden' : ''}`}`}
      >
        {/* InsuraHelp panel removed from the dialer — the InsuraBot "Helper"
            tab is now the single home for the knowledge-base assistant. */}

        {/* ═══ EAVESDROP / COACH MODE ═══ Agency owner listening / whispering / barging */}
        {eavesdropActive && (() => {
          // Mode-specific theme. Listen = red stealth (FBI). Whisper =
          // blue (manager → agent only). Barge = amber (manager → all).
          const isWhisper = eavesdropMode === 'whisper';
          const isBarge = eavesdropMode === 'barge';
          const isCoachMode = isWhisper || isBarge;
          const accent = isBarge
            ? { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500', dot100: 'bg-amber-100', borderHover: 'hover:bg-amber-100' }
            : isWhisper
              ? { bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500', dot100: 'bg-blue-100', borderHover: 'hover:bg-blue-100' }
              : { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500', dot100: 'bg-red-100', borderHover: 'hover:bg-red-100' };
          const HeaderIcon = isBarge ? Megaphone : isWhisper ? Mic : Radio;
          const BodyIcon = isBarge ? Megaphone : isWhisper ? Mic : Headphones;
          const headerLabel = isBarge ? 'Live Barge' : isWhisper ? 'Live Whisper' : 'Live Listening';
          const bodyVerb = isBarge ? 'Barging on call with' : isWhisper ? 'Whispering on call with' : 'Listening to call with';
          const stopLabel = isBarge ? 'Stop Barge' : isWhisper ? 'Stop Whisper' : 'Stop Listening';
          const statusBadge = isBarge
            ? { label: 'Barge — agent and lead hear you', cls: `${accent.bg} ${accent.text}` }
            : isWhisper
              ? { label: 'Whisper — only the agent hears you (lead can\'t hear)', cls: `${accent.bg} ${accent.text}` }
              : { label: 'Stealth Mode — Agent cannot see you', cls: `${accent.bg} ${accent.text}` };

          return (
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className={`dialer-head flex items-center justify-between px-4 py-3 border-b ${accent.bg} rounded-t-md`}>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <HeaderIcon className={`h-4 w-4 ${accent.text}`} />
                  <span className={`absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full ${accent.dot} animate-pulse`} />
                </div>
                <span className={`text-sm font-semibold ${accent.text}`}>{headerLabel}</span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={minimizeWidget} className={`p-1 ${accent.borderHover} rounded`}>
                  <ChevronDown className={`h-4 w-4 ${accent.text}`} />
                </button>
                <button onClick={stopEavesdrop} className={`p-1 ${accent.borderHover} rounded`}>
                  <X className={`h-4 w-4 ${accent.text}`} />
                </button>
              </div>
            </div>

            {/* Eavesdrop Content — scrollable so the Stop button at the bottom
                is always reachable, especially in whisper/barge mode where the
                push-to-talk button + helper text add height. Tighter spacing
                (gap-4 + p-4) than listen mode so we don't need to scroll most
                of the time. */}
            <div className="flex-1 flex flex-col items-stretch justify-start p-3.5 gap-3 overflow-y-auto">
              {/* COMPACT HEADER ROW (redesigned 2026-07-25).
                  Was a stacked block: 80px circle, name, verb, lead name, then a
                  separate duration row and a separate status pill — four vertical
                  sections of mostly whitespace in a 400px-wide widget. Now one
                  row: small avatar, names on two tight lines, timer right-aligned. */}
              <div className="flex w-full items-center gap-2.5">
                <div className={`relative h-9 w-9 shrink-0 rounded-full ${accent.dot100} flex items-center justify-center`}>
                  <BodyIcon className={`h-4 w-4 ${accent.text}`} />
                  <span className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ${accent.dot} border-2 border-white animate-pulse`} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-gray-900">{eavesdropAgentName}</p>
                  <p className="truncate text-xs text-gray-500">
                    {bodyVerb} <span className="font-medium text-gray-700">{eavesdropLeadName}</span>
                  </p>
                </div>
                {eavesdropStartedAt && (
                  <span className="shrink-0 font-mono text-xs tabular-nums text-gray-500">
                    {Math.floor((Date.now() - eavesdropStartedAt.getTime()) / 60000)}:
                    {String(Math.floor(((Date.now() - eavesdropStartedAt.getTime()) / 1000) % 60)).padStart(2, '0')}
                  </span>
                )}
              </div>

              {/* Real spectrum off the analyser, same visualizer as the console
                  rail. NOTE: `accent.viz` must NOT be applied here — it is a BAR
                  colour from the old div-based meter, and as a canvas class it
                  became a solid red BACKGROUND with the bars barely visible on
                  top (that's the flat red block). The canvas colours itself from
                  `variant`; the wrapper just supplies a neutral surface. */}
              <div className="w-full overflow-hidden rounded-md bg-gray-50 px-2 py-1.5 ring-1 ring-inset ring-gray-200/70">
                <CoachWaveform
                  readSpectrum={coachReadSpectrum}
                  variant={isCoachMode ? "whisper" : "listen"}
                  className="block h-9 w-full"
                />
              </div>

              {/* Single status line — mode + auto-follow share one row instead of
                  two stacked full-width pills. */}
              <div className="flex w-full flex-wrap items-center gap-1.5">
                <div className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-1 ${statusBadge.cls}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${accent.dot} animate-pulse`} />
                  <span className={`text-[11px] font-medium ${accent.text}`}>{statusBadge.label}</span>
                </div>
                {eavesdropFollowingAgentId && (
                  <div className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 py-1">
                    <Eye className="h-3 w-3 text-blue-500" />
                    <span className="text-[11px] font-medium text-blue-600">Auto-following</span>
                  </div>
                )}
              </div>

              {/* Volume + output/mic pickers — identical control to the console
                  rail, reading the same session state. */}
              <CoachAudioControls showMic={isCoachMode} compact />

              {/* Push-to-talk button — only in whisper / barge.
                  Dispatches window events that LiveCalls listens for and
                  routes through the actual mic capture. Spacebar shortcut
                  also still works (registered globally in LiveCalls). */}
              {isCoachMode && (
                <div className="w-full flex flex-col gap-2">
                  <button
                    onClick={coachToggleMic}
                    aria-pressed={coachMicActive}
                    className={`relative w-full rounded-lg py-3 px-4 font-semibold text-sm transition-all select-none ${
                      coachMicActive
                        ? isBarge
                          ? 'bg-amber-600 text-white shadow-lg shadow-amber-500/40 scale-[0.98]'
                          : 'bg-blue-600 text-white shadow-lg shadow-blue-500/40 scale-[0.98]'
                        : 'bg-white border-2 border-gray-300 text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <Mic className={`h-4 w-4 ${coachMicActive ? '' : 'text-gray-500'}`} />
                      <span>
                        {coachMicActive
                          ? `Mic live — click to mute`
                          : `Click to talk ${isBarge ? 'to agent + lead' : 'to agent only'}`}
                      </span>
                    </div>
                    {/* Mic level meter while transmitting */}
                    {coachMicActive && (
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/30 rounded-b-lg overflow-hidden">
                        <div
                          className="h-full bg-white transition-all duration-75"
                          style={{ width: `${Math.min(100, coachMicVolume * 100)}%` }}
                        />
                      </div>
                    )}
                  </button>
                  <p className="text-[10px] text-center text-gray-500">
                    Hold space or button to talk
                  </p>
                </div>
              )}
            </div>

            {/* Stop Button */}
            <div className="p-4 border-t">
              <button
                onClick={stopEavesdrop}
                className={`w-full flex items-center justify-center gap-2 px-4 py-3 ${accent.dot} hover:opacity-90 text-white rounded-lg font-semibold transition-colors`}
              >
                <PhoneOff className="h-4 w-4" />
                {stopLabel}
              </button>
            </div>
          </div>
          );
        })()}

        {/* Header - styled like InsuraBot */}
        <div className={`dialer-head flex items-center justify-between px-4 py-3 border-b bg-muted/50 rounded-t-md ${eavesdropActive ? 'hidden' : ''}`}>
          <div className="flex items-center gap-1.5">
            {/* Icon — animated SVG for active calls, static icon for idle */}
            {/* 24px, not 20: the connected state is a full figure (head, shoulders,
                handset) and 20px doesn't have the pixels to keep those apart. The
                idle icon inside stays its own size and just centres. */}
            <span
              className="relative inline-flex items-center justify-center shrink-0 w-6 h-6"
              title={
                hasActiveCall
                  ? `Call: ${callStatus || 'active'}`
                  : isReconnecting
                    ? 'Reconnecting...'
                    : deviceError
                      ? `Error: ${deviceError}`
                      : isDeviceReady
                        ? 'Ready for Calling'
                        : 'Initializing...'
              }
            >
              {(hasActiveCall || (sessionActive && dialerMode === 'multiline')) ? (
                <AnimatedCallIcon
                  status={(sessionActive && dialerMode === 'multiline') ? 'in-progress' : (callStatus || 'in-progress')}
                  color={isReconnecting ? '#f97316' : deviceError ? '#ef4444' : isDeviceReady ? '#10b981' : '#eab308'}
                />
              ) : (
                <span
                  className={`relative transition-all duration-300 ${
                    isReconnecting
                      ? 'text-orange-500 animate-pulse'
                      : deviceError
                        ? 'text-red-500'
                        : isDeviceReady
                          ? 'text-emerald-600'
                          : 'text-yellow-500 animate-pulse'
                  }`}
                >
                  {getHeaderIcon()}
                </span>
              )}
            </span>
            <h3 className="font-semibold">{getHeaderTitle()}</h3>
          </div>
          <div className="flex items-center space-x-1">
            {/* Trial talk-time allowance — first in the row, trial accounts only */}
            <TrialDialTimePill compact />
            {/* Auto-open lead details toggle */}
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`h-7 w-7 p-0 hover:bg-primary/10 ${
                      autoOpenLeadDetails ? 'bg-primary/10 text-primary !bg-primary/10' : 'text-gray-500'
                    }`}
                    onClick={() => setAutoOpenLeadDetails(prev => !prev)}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Auto open lead details: {autoOpenLeadDetails ? 'On' : 'Off'}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {/* Audio Device Settings - show during active call */}
            {hasActiveCall && !isBridgedCall && audioInputDevices && audioOutputDevices && (
              <AudioDeviceSelector
                audioInputDevices={audioInputDevices}
                audioOutputDevices={audioOutputDevices}
                selectedInputDeviceId={selectedInputDeviceId ?? null}
                selectedOutputDeviceId={selectedOutputDeviceId ?? null}
                onAudioInputChange={setAudioInputDevice}
                onAudioOutputChange={setAudioOutputDevice}
                onRefreshDevices={refreshAudioDevices}
                microphoneAudioLevel={microphoneAudioLevel}
                isHardwareMuted={isHardwareMuted}
                isMuted={isMuted}
                compact={true}
                speakerVolume={speakerVolume}
                onSpeakerVolumeChange={setSpeakerVolume}
                onStartAudioMonitoring={startAudioMonitoring}
                onStopAudioMonitoring={stopAudioMonitoring}
              />
            )}
            {/* Session History button — only show when idle (no active call/session) */}
            {!hasActiveCall && !sessionActive && (
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setCurrentView(currentView === 'history' ? 'keypad' : 'history')}
                      className={`h-7 w-7 p-0 ${currentView === 'history' ? 'bg-primary/10 text-primary' : ''}`}
                    >
                      <Clock className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Session history</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {/* Script panel button */}
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const next = !isScriptPanelOpen;
                      setIsScriptPanelOpen(next);
                      // Phone: the script sheet and the dialer would stack —
                      // minimize the dialer (works mid-call; the minimized
                      // pill keeps the call reachable).
                      if (next && isMobileShell) minimizeWidget();
                    }}
                    className={`h-7 w-7 p-0 ${isScriptPanelOpen ? 'bg-primary/10 text-primary' : ''}`}
                  >
                    <FileText className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Call scripts</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {/* InsuraHelp button removed — assistant now lives in the InsuraBot
                "Helper" tab. */}
            {/* Minimize button */}
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={minimizeWidget}
            >
              <ChevronDown className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {/* Device reconnecting banner — visible while we attempt to restore.
            Calls are disabled here because isDeviceReady=false while reconnecting,
            but we make that explicit to the agent so they understand the freeze. */}
        {isReconnecting && (
          <div className="px-4 py-2 bg-orange-50 border-b border-orange-200 text-orange-700 text-xs flex items-start gap-2">
            <div className="w-3 h-3 mt-0.5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
            <div className="flex-1 leading-snug">
              <div className="font-medium">Network connection lost — reconnecting…</div>
              <div className="text-orange-600/80 mt-0.5">Calls are disabled until the dialer turns green again. Usually 5-15 seconds.</div>
            </div>
          </div>
        )}

        {/* Device error banner — shown when auto-reconnect gave up.
            Explains WHY this happens (network drop / device sleep) and gives the
            agent two clear actions: Reconnect (one-tap) or Refresh page (last resort). */}
        {deviceError && !isReconnecting && (
          <div className="px-4 py-2 bg-red-50 border-b border-red-200 text-red-700 text-xs flex items-start gap-2">
            <span className="h-2 w-2 mt-1 rounded-full bg-red-500 flex-shrink-0" />
            <div className="flex-1 leading-snug">
              <div className="font-medium">Dialer disconnected — calls are paused</div>
              <div className="text-red-600/80 mt-0.5">
                This usually means your network (WiFi or internet) dropped, your device went to sleep, or you&apos;re on an iPad/tablet during a long session (Safari/iOS suspends background connections). Tap{' '}
                <span className="font-semibold">Reconnect</span> to restore. If that doesn&apos;t work, refresh the page.
              </div>
              <div className="mt-1.5 flex gap-1.5">
                <button
                  onClick={reconnectDevice}
                  className="px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded text-xs font-medium transition-colors"
                >
                  Reconnect
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="px-2 py-1 bg-white hover:bg-gray-50 text-red-700 border border-red-200 rounded text-xs font-medium transition-colors"
                >
                  Refresh Page
                </button>
              </div>
            </div>
          </div>
        )}

        {/* No active number: the reason the dialer can't place calls, stated
            plainly with the way out — not a "disconnected" guess. */}
        {dialBlocked && dialBlocker && !isReconnecting && !deviceError && (
          <DialBlockerNotice blocker={dialBlocker} onRecheck={recheckPhoneNumbers} rechecking={isCheckingPhoneNumbers} />
        )}

        {/* Session Recovery Prompt */}
        {pendingRecovery && (
          <div className="flex-1 flex items-center justify-center p-6">
            <div className="text-center space-y-4 max-w-[280px]">
              <div className="mx-auto w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Phone className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold">Resume Dialing Session?</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  You had an active session with {selectedLeads.length} lead{selectedLeads.length !== 1 ? 's' : ''}. Resume where you left off?
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={declineRecovery}
                  className="flex-1 px-3 py-2 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50 transition-colors"
                >
                  No, Start Fresh
                </button>
                <button
                  onClick={confirmRecovery}
                  className="flex-1 px-3 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                >
                  Yes, Resume
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        {/* Call-waiting overlay vs dialer session:
            When the agent presses "Answer" / "Hold & Answer" / "Merge" on an
            incoming call during an active multi-line or power-dialer session,
            we show the Active Call UI. CRITICAL: we keep PowerDialerSession /
            MultiLineDialerSession MOUNTED via CSS hide (display:none), not
            conditional unmount. Unmounting wipes their `isPaused`,
            `awaitingMultiLineDisposition`, lead queue, and line state — so
            after the incoming call ends and the dialer view comes back, the
            "Resume Multi-Line" / "Resume Power Dialer" button never appears
            and the queue is gone. Hiding preserves all that state.

            We deliberately do NOT require `hasActiveCall` for the Active Call
            overlay — the SDK transition (disconnect master → connect incoming)
            takes 250-500ms, during which activeCall is briefly null. Without
            the overlay there, the multi-line widget would flash through that
            gap. The Active Call UI handles a missing activeCall gracefully
            (just shows the connecting/empty state until the SDK lands). */}
        {(() => {
          const showActiveCallOverlay =
            isInCallWaiting ||
            (sessionActive && hasActiveCall && activeCallSource === 'manual');
          return (
            <>
              {/* Active Call UI overlay (call-waiting OR manual-call-during-session) */}
              {showActiveCallOverlay && renderActiveCall()}

              {/* Dialer session — kept mounted to preserve state, hidden when overlay shows */}
              {sessionActive && dialerMode === 'power' && (
                <div
                  className="flex-1 overflow-y-auto"
                  style={showActiveCallOverlay ? { display: 'none' } : undefined}
                >
                  <PowerDialerSession
                    leads={selectedLeads}
                    aiMatchingEnabled={aiMatchingEnabled}
                    onStop={handleStopDialer}
                    onComplete={handleDialerComplete}
                    hideLeadDetails={true}
                    autoOpenLeadDetails={autoOpenLeadDetails}
                  />
                </div>
              )}
              {sessionActive && dialerMode === 'multiline' && (
                <div
                  className="flex-1 overflow-y-auto"
                  style={showActiveCallOverlay ? { display: 'none' } : undefined}
                >
                  <MultiLineDialerSession
                    leads={selectedLeads}
                    aiMatchingEnabled={aiMatchingEnabled}
                    onStop={handleStopDialer}
                    onComplete={handleDialerComplete}
                    hideLeadDetails={true}
                    autoOpenLeadDetails={autoOpenLeadDetails}
                    resumeOffset={resumeOffset}
                    totalLeadsOriginal={resumeOffset > 0 ? selectedLeads.length + resumeOffset : undefined}
                  />
                </div>
              )}
            </>
          );
        })()}
        {/* Show other views only when no active session, no pending recovery,
            AND no call-waiting render already happened above. Without the
            `!isInCallWaiting` guard, a manual call with a held call matches
            BOTH the call-waiting branch above AND this one → Active Call UI
            renders twice, stacked. */}
        {!sessionActive && !pendingRecovery && !isInCallWaiting && (
          /* ACTIVE CALL OUTRANKS SESSION COMPLETE (2026-07-23): a send-back
             accepted right after the agent's last-lead session completed left
             the live call hidden UNDER the "Session Complete!" screen — the
             agent thought the call died. Any live call renders first; the
             complete screen (state untouched) returns when the call ends. */
          hasActiveCall ? (
            renderActiveCall()
          ) : showSessionComplete ? (
            renderSessionComplete()
          ) : currentView === 'history' ? (
            <div className="flex-1 min-h-0 overflow-hidden">
              <DialerSessionHistory
                onBack={() => setCurrentView('keypad')}
                onResume={handleResumeSession}
              />
            </div>
          ) : currentView === 'queue' ? (
            renderQueue()
          ) : (
            renderKeypad()
          )
        )}

        {/* No Phone Number Dialog */}
        <NoPhoneNumberDialog
          open={showNoPhoneDialog}
          onOpenChange={setShowNoPhoneDialog}
          blocker={dialBlocker}
        />
      </div>

      {/* Script Panel — free-floating draggable window.
          ⚠️ PORTALED to <body>: this block sits nested inside the dialer's
          panel div, whose translate makes it the containing block for the
          panel's position:fixed — on the shell the "fixed" sheet rendered
          inside the dialer box and slid off-screen with it when minimized
          ("call scripts show nothing"). A body portal ends that for good. */}
      {isScriptPanelOpen && typeof document !== "undefined" && createPortal(
        <ScriptPanel
          scripts={scripts}
          selectedScriptId={selectedScriptId}
          onSelectScript={setSelectedScriptId}
          onClose={() => setIsScriptPanelOpen(false)}
          activeLead={activeCallLead || viewedLead}
        />,
        document.body,
      )}

      {/* No Sale Reason Modal for manual call disposition */}
      <NoSaleReasonModal
        isOpen={showWidgetNoSaleModal}
        onOpenChange={setShowWidgetNoSaleModal}
        onConfirm={handleWidgetNoSaleConfirm}
      />
      {dncModal}
    </>
  );
}

// ────────────────────────────────────────────────────────────
// DialIQ — Threshold Toast listener
// Mounts once inside UnifiedDialerWidget. Listens to the
// `dialiq:threshold` window event dispatched by dialerSessionApi.saveCallResult
// whenever the backend reports a 5x no-answer window advance.
// ────────────────────────────────────────────────────────────
function DialiqThresholdListener() {
  const [event, setEvent] = useState<ThresholdEvent | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as any;
      if (!detail) return;
      setEvent({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        leadId: detail.leadId || "",
        leadName: detail.leadName || null,
        leadState: detail.leadState || null,
        fromWindow: detail.fromWindow,
        toWindow: detail.toWindow ?? null,
        terminal: !!detail.terminal,
      });
    };
    window.addEventListener("dialiq:threshold", handler);
    return () => window.removeEventListener("dialiq:threshold", handler);
  }, []);

  return (
    <RedialThresholdToast
      event={event}
      onDismiss={() => setEvent(null)}
      positionClass="fixed right-4 top-20 z-[60]"
    />
  );
}

export default UnifiedDialerWidget;
